/**
 * @keystone/sim — SHARED CONTRACTS
 *
 * Every type that crosses a module boundary lives here. All builders code against
 * this file. Rules (see ARCHITECTURE.md):
 *   - Additive changes only (new optional fields, new union members). Never rename,
 *     remove, or change the meaning of an existing field.
 *   - Keep everything JSON-serializable: no classes, Maps, Sets, functions or
 *     undefined-vs-missing semantics inside World / defs.
 *   - Units: distance in yards, time in integer milliseconds, angles in radians
 *     (0 = +x axis, counter-clockwise positive), percentages as fractions
 *     (0.3 = 30%) unless a field name ends in `Pct` AND says otherwise.
 */

// ============================================================================
// 1. Primitives
// ============================================================================

/** Integer milliseconds. Sim time starts at 0 when the sim is created. */
export type Ms = number;
/** Yards. */
export type Yards = number;
/** Radians. 0 = +x, counter-clockwise positive. */
export type Radians = number;

/** Unit instance id, unique within a World. Players: "p1".."p4". NPCs: "n<seq>". */
export type UnitId = string;
/** Ability definition id, e.g. "warden.shield_slam", "npc.venom_bolt". "melee" is the auto-attack. */
export type AbilityId = string;
/** Aura definition id, e.g. "warden.last_stand". */
export type AuraId = string;
/** Aura *instance* id (unique per application), e.g. "a123". */
export type AuraInstanceId = string;
export type ClassId = string;
export type NpcId = string;
export type EncounterId = string;
export type DungeonId = string;
export type PackId = string;
export type RoomId = string;
export type ResourceId = 'mana' | 'rage' | 'resolve' | 'maelstrom' | 'holy_power' | (string & {});
export type TelegraphId = string;
export type ZoneId = string;
export type AffixId = 'fortified' | 'tyrannical' | (string & {});

/** The auto-attack ability id. Exported to WCL as abilityGameID 1. */
export const MELEE_ABILITY_ID: AbilityId = 'melee';
/** Fixed simulation step. */
export const TICK_MS: Ms = 50;
/** Spell-queue window before GCD/cast end (PLAN §8). */
export const SPELL_QUEUE_WINDOW_MS: Ms = 400;
/** Base global cooldown and its haste clamp (PLAN §3). */
export const BASE_GCD_MS: Ms = 1500;
export const MIN_GCD_MS: Ms = 750;
/** AI re-picks its victim this often (PLAN §3). */
export const THREAT_RESELECT_MS: Ms = 1000;
/** Challenger must exceed victim threat by these factors (melee / ranged). */
export const THREAT_OVERTAKE_MELEE = 1.1;
export const THREAT_OVERTAKE_RANGED = 1.3;
/** CC diminishing returns reset after this long without a new CC of that category. */
export const DR_RESET_MS: Ms = 18000;
/** Successive DR multipliers; index >= length means immune. */
export const DR_MULTIPLIERS: readonly number[] = [1, 0.5, 0.25];
/** Taunt DR on flagged mobs. */
export const TAUNT_DR_MULTIPLIERS: readonly number[] = [1, 0.65, 0.42, 0.27];
/** Cast pushback per hit and max number of pushbacks (PLAN §3). */
export const CAST_PUSHBACK_MS: Ms = 500;
export const MAX_PUSHBACKS = 2;
/** Channel loses this fraction of its total duration per hit, max MAX_PUSHBACKS. */
export const CHANNEL_PUSHBACK_FRACTION = 0.25;
/** Player combat reach (yards). Melee range = max(5, reachA + reachB + 4/3). */
export const PLAYER_COMBAT_REACH: Yards = 1.5;
export const MELEE_RANGE_BONUS: Yards = 4 / 3;
export const MIN_MELEE_RANGE: Yards = 5;
/** Extra range leeway when both units are moving. */
export const MOVING_LEEWAY: Yards = 8 / 3;
/**
 * Backpedal speed as a fraction of run speed (WoW: 4.5 / 7). A human-controlled unit moving by
 * keys against its facing (dir · facing < 0) moves this much slower; bots and NPCs never do.
 */
export const BACKPEDAL_FACTOR = 4.5 / 7;

/** Melee front arc (120 degrees). */
export const MELEE_ARC: Radians = (2 * Math.PI) / 3;

// ---- +autoattack: WoW-retail auto-attacks (combat/melee.ts) ----
/** The Ranger's ranged auto-attack (Auto Shot). Melee swings (main and off hand) use MELEE_ABILITY_ID. */
export const AUTO_SHOT_ABILITY_ID: AbilityId = 'auto_shot';
/** Auto-attacks need the target in the front half (180°), as in retail. */
export const AUTO_ATTACK_ARC: Radians = Math.PI;
/** Auto Shot: the first shot after starting waits this long (draw). */
export const AUTO_SHOT_WINDUP_MS: Ms = 500;
/** Extra miss chance of a dual wielder's white swings (both hands), retail's 19%. */
export const DUAL_WIELD_MISS = 0.19;
/** Auto Shot range when the class weapon gives none (yards, edge to edge). */
export const AUTO_SHOT_RANGE: Yards = 40;
/** Mob room-boundary evade check interval. */
export const EVADE_CHECK_MS: Ms = 2500;

// ---- +patrols: out-of-combat movement and WoW social aggro (docs/design/mob-movement.md) ----
/** NPC walk speed as a share of run speed (WoW creature walk 2.5 yd/s vs run 7 yd/s). */
export const NPC_WALK_FRACTION = 2.5 / 7;
/** Call for help: idle NPCs within this distance of a mob entering combat (and in its LoS) join (TC Creature.Family.Assistance.Radius). */
export const ASSIST_RADIUS: Yards = 10;
/** Call for help: helpers attack this long after the call (TC CREATURE_FAMILY_ASSISTANCE_DELAY). */
export const ASSIST_DELAY_MS: Ms = 1500;
/** Opt-in trash evades once this far from where it entered combat (TC CONFIG_THREAT_RADIUS default). */
export const LEASH_RADIUS: Yards = 60;
/** Wanderers pause this long (uniform range, ms) after a burst of moves (TC urand(4,10) s; cMaNGOS 3–10 s). */
export const WANDER_PAUSE_MS: readonly [Ms, Ms] = [4000, 10000];
/** Wanderers chain this many moves (uniform, inclusive) before pausing (cMaNGOS urand(1,3); AzerothCore averages ≈ 2). */
export const WANDER_BURST: readonly [number, number] = [1, 3];
/** Idle (opt-in) mobs turn a little this often (uniform range, ms); purely cosmetic facing. */
export const FIDGET_MS: readonly [Ms, Ms] = [6000, 16000];

export interface Vec2 {
  x: Yards;
  y: Yards;
}

/** Axis-aligned rectangle (min corner + size). */
export interface Rect {
  x: Yards;
  y: Yards;
  w: Yards;
  h: Yards;
}

/** Simple polygon, counter-clockwise, not self-intersecting. */
export interface Polygon {
  points: Vec2[];
}

// ============================================================================
// 2. Seeded RNG (the ONLY randomness allowed in the sim)
// ============================================================================

/** Serializable RNG state (sfc32: four uint32 words). Lives in World.rngState. */
export type RngState = [number, number, number, number];

/** Deterministic PRNG. All methods advance the state stored in the World. */
export interface Rng {
  /** Uniform float in [0, 1). */
  next(): number;
  /** Uniform integer in [min, max] inclusive. */
  int(min: number, max: number): number;
  /** Uniform float in [min, max). */
  range(min: number, max: number): number;
  /** true with probability p (0..1). */
  chance(p: number): boolean;
  /** Uniform pick; undefined for an empty array. */
  pick<T>(arr: readonly T[]): T | undefined;
  /** Copy of the current state. */
  getState(): RngState;
  setState(s: RngState): void;
}

// ============================================================================
// 3. Stats, schools, CC
// ============================================================================

export type School = 'physical' | 'holy' | 'fire' | 'nature' | 'frost' | 'shadow' | 'arcane';
export type DispelType = 'magic' | 'poison' | 'curse' | 'disease' | 'enrage';
/** CC categories for diminishing returns. On NPCs only 'stun' (and taunt DR) apply (PLAN §3). */
export type CcType = 'stun' | 'incapacitate' | 'disorient' | 'silence' | 'root';
export type Role = 'tank' | 'healer' | 'dps';
export type Faction = 'party' | 'enemy';
export type UnitKind = 'player' | 'npc';
/** Who issues Commands for a unit. 'ai' = NPC AISystem. */
export type Controller = 'human' | 'bot' | 'ai';
export type NpcTier = 'fodder' | 'standard' | 'caster' | 'big' | 'miniboss' | 'boss';

/** Base (unbuffed) stats. Fractions, not percents: crit 0.1 = 10%. */
export interface Stats {
  /** Generic attack/spell power. Ability coefficients multiply this. */
  power: number;
  /** Haste fraction: 0.2 → casts/GCD/swings/hasted ticks are divided by 1.2. */
  haste: number;
  /** Crit chance fraction. Crits deal CRIT_MULTIPLIER. */
  crit: number;
  /** Mastery-lite: a class-defined generic bonus fraction (content decides meaning via conditions/mods). */
  mastery: number;
  /** Versatility-like flat damage/heal done bonus and damage taken reduction/2. */
  versatility: number;
  /** Physical damage reduction fraction from armor (0..0.85). */
  armor: number;
  /** Avoidance vs melee from the front (fractions). Parry/block are impossible from behind. */
  dodge: number;
  parry: number;
  block: number;
  /** Fraction of a blocked hit that is negated. */
  blockAmount: number;
  /** Chance for this unit's melee to miss. */
  missChance: number;
}

export const CRIT_MULTIPLIER = 2;

/**
 * Stat modifiers an aura can apply. `value` semantics:
 *  - *Pct stats are additive fractions (0.1 = +10%, -0.4 = -40%) summed then applied
 *    multiplicatively per-aura for damageTakenPct (i.e. product of (1+v)).
 *  - flat stats add to the Stats field of the same name.
 */
export type StatModKind =
  | 'damageTakenPct' // multiplicative per aura: taken *= (1 + v). Optional `school` filter.
  | 'damageDonePct' // multiplicative per aura. Optional `school` filter.
  | 'healingDonePct'
  | 'healingTakenPct'
  | 'hastePct' // additive to Stats.haste
  | 'critPct' // additive to Stats.crit
  | 'masteryPct'
  | 'powerPct' // multiplicative on Stats.power
  | 'moveSpeedPct' // multiplicative per aura; slows are negative. Max of roots → 0.
  | 'threatPct' // multiplicative threat generated (tank aura ≈ +3..+4 i.e. ×4..×5)
  | 'castTimePct' // multiplicative on cast time; -1 = instant. Optional `abilityIds` filter.
  | 'cooldownRatePct' // cooldown recovery speed; +1 = recovers twice as fast. Optional `abilityIds`.
  | 'resourceCostPct'
  | 'critChanceBonus' // additive crit chance for matching abilityIds (1 = guaranteed crit)
  | 'dodge'
  | 'parry'
  | 'block'
  | 'armor'
  | 'maxHpPct' // +basekits: multiplicative per aura on max HP (Hearthward); current HP moves with it
  | 'leechPct' // +specs: heal the holder for this fraction of the damage it deals to hostiles
  | 'gatherSpeedPct'; // +grid: gather channels finish faster: channel ÷ (1 + Σv) (field/gather.ts)

export interface StatMod {
  stat: StatModKind;
  value: number;
  /** Only applies to damage/healing of this school. */
  school?: School;
  /** Only applies to these abilities (castTimePct, cooldownRatePct, critChanceBonus, damageDonePct...). */
  abilityIds?: AbilityId[];
  /** +lead: rotation rework (F8). The value is NOT multiplied by the aura's stacks (flat bonus on a "charges" aura). */
  ignoreStacks?: boolean;
  /**
   * +specs: the value scales with the holder's missing health: value × (1 − hp/maxHp) (Bloodreaver's
   * Thirst: +crit and +haste the lower you are). Combines with stacks.
   */
  perMissingHp?: boolean;
}

/** Final derived stats after auras. Produced by CombatSystem.effectiveStats. */
export interface EffectiveStats extends Stats {
  moveSpeed: Yards; // yards/sec after mods (0 if rooted/stunned)
  damageDoneMult: number;
  damageTakenMult: number; // excluding school-specific mods
  healingDoneMult: number;
  healingTakenMult: number;
  threatMult: number;
}

/** Control state derived from auras (CC, immunities). Produced by CombatSystem.controlState. */
export interface ControlState {
  canMove: boolean; // false when stunned/rooted/incapacitated/disoriented
  canCast: boolean; // false when stunned/incapacitated/disoriented/silenced
  canAttack: boolean; // melee swings allowed
  canAct: boolean; // false when stunned/incapacitated/disoriented (no commands at all except none)
  immuneDamage: boolean;
  immuneCc: boolean;
  /** Any active CC types on the unit. */
  cc: CcType[];
}

// ============================================================================
// 4. Resources
// ============================================================================

export interface ResourceDef {
  id: ResourceId;
  name: string;
  max: number;
  /** Starting value on spawn / respawn. */
  start: number;
  /** Passive regen per second (negative = decay). */
  regenPerSec?: number;
  /** Regen per second while out of combat (overrides regenPerSec out of combat). */
  regenPerSecOoc?: number;
  /**
   * +basekits: out of combat the pool drifts toward this value (up or down) at |regenPerSecOoc| per
   * second instead of regenerating past it (the Warrior's Grit rests at 20, so Breaker can open).
   */
  oocSettle?: number;
  /** Generation hooks (e.g. Resolve from hits taken, Rage from melee). */
  gainOn?: ResourceGainRule[];
}

export interface ResourceGainRule {
  on: 'damageTaken' | 'damageDealt' | 'meleeSwingLanded' | 'critDealt';
  /** Flat amount per event, or per 1% of max HP when perPctMaxHp is true. */
  amount: number;
  perPctMaxHp?: boolean;
}

export interface ResourcePool {
  cur: number;
  max: number;
}

export interface ResourceAmount {
  resource: ResourceId;
  amount: number;
}

// ============================================================================
// 5. Shapes and targeting
// ============================================================================

/** Where an area is anchored when it is created. */
export type AreaAnchor =
  | 'caster' // caster position (snapshotted unless `follow`)
  | 'target' // primary target position (snapshotted unless `follow`)
  | 'point'; // the ground point given in the cast

/** Area shapes. Cone/line point along the caster→target (or caster facing if no target) direction. */
export type AreaShape =
  | { type: 'circle'; radius: Yards }
  | { type: 'ring'; inner: Yards; outer: Yards }
  | { type: 'cone'; range: Yards; angle: Radians }
  | { type: 'line'; length: Yards; width: Yards }
  | { type: 'rect'; w: Yards; h: Yards };

/** A fully resolved shape placed in the world (what telegraphs and hit tests use). */
export interface WorldShape {
  shape: AreaShape;
  origin: Vec2;
  /** Direction for cone/line/rect; ignored for circle/ring. */
  facing: Radians;
}

/** Which units an effect or area affects relative to the source. */
export type Affects = 'enemies' | 'allies' | 'all' | 'allies_not_self';

/** How a castable ability chooses its primary target. */
export type Targeting =
  | 'self' // always the caster
  | 'ally' // friendly unit (defaults to self if none selected)
  | 'enemy' // hostile unit, must be selected/valid
  | 'ground' // a point within range
  | 'cone' // no target, frontal cone from caster facing
  | 'none' // no target (e.g. self-centred AoE)
  | 'any'; // +integrator: friendly OR hostile unit (Holy Shock); falls back to self like 'ally'

/** Where an effect lands, relative to the ability's resolved target. */
export interface AreaSpec {
  anchor: AreaAnchor;
  shape: AreaShape;
  affects: Affects;
  /** Cap on units hit (closest first, deterministic tie-break by unit id). */
  maxTargets?: number;
  /** Include the primary target even if outside the shape. Default true for 'target' anchor. */
  includePrimary?: boolean;
  /** +integrator: never hit the primary target (cleave "other nearby enemies"). Overrides includePrimary. */
  excludePrimary?: boolean;
  /**
   * +mage-specs (S11): cap units hit at floor(spent ÷ per) + bonus, where `spent` is what the cast's
   * `AbilityDef.spendAll` took (Skybreak: one chain jump per 25 Voltage). Combines with maxTargets.
   */
  perSpentTargets?: { per: number; bonus?: number };
  /**
   * +ranger-specs: order the units in the area by health fraction, lowest first (ties: unit order),
   * before `maxTargets` cuts the list (Overgrowth Surge, Thorn Whip's bud on the most injured ally).
   */
  lowestHp?: boolean;
}

// ============================================================================
// 6. Conditions and amounts (used by effects, abilities, bots)
// ============================================================================

export type Condition =
  | { type: 'targetHpBelow'; pct: number }
  | { type: 'targetHpAbove'; pct: number }
  | { type: 'selfHpBelow'; pct: number }
  | { type: 'selfHasAura'; auraId: AuraId; minStacks?: number }
  | { type: 'selfLacksAura'; auraId: AuraId }
  | { type: 'targetHasAura'; auraId: AuraId; fromSelf?: boolean; /** +lead: rotation rework (F7) */ minStacks?: number }
  | { type: 'targetLacksAura'; auraId: AuraId; fromSelf?: boolean }
  | { type: 'resourceAtLeast'; resource: ResourceId; amount: number }
  | { type: 'targetIsCasting'; interruptibleOnly?: boolean }
  | { type: 'enemiesNearSelf'; radius: Yards; atLeast: number }
  | { type: 'chance'; p: number }
  | { type: 'targetIsEnemy' } // +integrator: target is hostile to the source
  | { type: 'targetIsSelf' } // +integrator: target is the source (e.g. caster-only zone aura)
  /** +basekits: the source is in combat (players: on an engaged NPC's threat table). */
  | { type: 'selfInCombat' }
  /**
   * +ranger-specs (S8, the Deadeye's perfect reload): the source carries this aura and it was applied
   * at least `minMs` and at most `maxMs` ago (either bound optional). A refresh keeps the first
   * application time. Deterministic: the press's tick decides it.
   */
  | { type: 'selfAuraElapsed'; auraId: AuraId; minMs?: Ms; maxMs?: Ms }
  | { type: 'and'; all: Condition[] }
  | { type: 'or'; any: Condition[] }
  | { type: 'not'; cond: Condition };

/**
 * A damage/heal/absorb magnitude. All present terms are summed, then multiplied by
 * source/target modifiers. `coef` multiplies the source's effective power.
 */
export interface Amount {
  base?: number;
  coef?: number;
  /** Fraction of the *source's* max HP. */
  pctSourceMaxHp?: number;
  /** Fraction of the *target's* max HP. */
  pctTargetMaxHp?: number;
  /** +specs: fraction of the *source's* missing HP (maxHp − hp) at resolution (Bloodwrench's heal). */
  pctSourceMissingHp?: number;
  /** Fraction of damage the *source* took in the last `windowMs` (Bulwark Strike). */
  pctRecentDamageTaken?: { pct: number; windowMs: Ms; minPctMaxHp?: number };
  /** ±fraction uniform variance (0.05 = ±5%). Rolled with the sim RNG. */
  variance?: number;
  /**
   * +lead: rotation rework (F7). Amount × (1 + mult × stacks), stacks read at resolution for each
   * affected unit: `on: 'self'` reads the source's aura, `'target'` the affected unit's. `fromSelf`
   * (default true) counts only instances cast by the source. Consumption stays an explicit removeAura.
   */
  perStack?: AmountPerStack;
  /**
   * +mage-specs (S11): amount × (1 + perSpent × spent), `spent` = the points the cast's
   * `AbilityDef.spendAll` took (0 outside such a cast). Skybreak, Hoarlance.
   */
  perSpent?: number;
}

/** +lead: rotation rework (F7). See Amount.perStack. */
export interface AmountPerStack {
  auraId: AuraId;
  on: 'self' | 'target';
  mult: number;
  fromSelf?: boolean;
}

// ============================================================================
// 7. Effects (table-driven DSL; PLAN §4/§8)
// ============================================================================

/** Fields common to every effect. */
export interface EffectBase {
  /**
   * Apply to the primary target (default), the caster, or every unit in an area.
   * +ranger-specs (S7 companions): 'companion' = the caster's living companion (none: skipped).
   */
  to?: 'target' | 'self' | 'area' | 'companion';
  /** Required when to === 'area'. */
  area?: AreaSpec;
  /** Effect only runs if this holds (evaluated per affected unit, source = caster). */
  if?: Condition;
}

export interface DamageEffect extends EffectBase {
  type: 'damage';
  school: School;
  amount: Amount;
  /** Extra crit chance for this hit (1 = always crits). */
  critBonus?: number;
  /** Threat multiplier for this hit (Cleave Strike ×2). */
  threatMult?: number;
  /** Split damage evenly among all units hit (e.g. "shared" mechanics). */
  split?: boolean;
  /** WCL isAoE flag override (defaults to to === 'area'). */
  isAoE?: boolean;
  /** Can be avoided by dodge/parry/block (melee-style). Default: school === 'physical' && not area. */
  avoidable?: boolean;
  /**
   * +specs: deliberate self-harm (Bloodletting): never crits, ignores every mitigation and absorb,
   * generates no threat, and can never kill (leaves at least 1 HP). Still counts as damage taken
   * (resource gain rules, damageTaken procs).
   */
  selfHarm?: boolean;
}

export interface HealEffect extends EffectBase {
  type: 'heal';
  amount: Amount;
  critBonus?: number;
  /** Smart heal: pick the N lowest-HP% allies in the area instead of all. */
  smart?: number;
}

export interface AbsorbEffect extends EffectBase {
  type: 'absorb';
  amount: Amount;
  /** The aura that carries the shield (its def must set `absorb`). */
  auraId: AuraId;
}

export interface ApplyAuraEffect extends EffectBase {
  type: 'applyAura';
  /** Inline definition; the registry indexes it by aura.id. Reuse the same id for the same aura. */
  aura: AuraDef;
  /** Stacks to add (default 1). */
  stacks?: number;
  /** +encounters: overrides the def's duration for this application (e.g. Tyrannical −20%). */
  durationMs?: Ms;
}

export interface RemoveAuraEffect extends EffectBase {
  type: 'removeAura';
  auraId: AuraId;
  /** Remove only this many stacks (default: whole aura). */
  stacks?: number;
  /** Only the caster's own instance (default true). */
  fromSelf?: boolean;
}

export interface InterruptEffect extends EffectBase {
  type: 'interrupt';
  /** School lockout applied to the interrupted unit's cast school. */
  lockoutMs: Ms;
}

export interface DispelEffect extends EffectBase {
  type: 'dispel';
  dispelTypes: DispelType[];
  /** Remove harmful auras from allies (default) or helpful auras from enemies (purge). */
  harmful: boolean;
  /** Max auras removed (default 1). */
  count?: number;
}

export interface TauntEffect extends EffectBase {
  type: 'taunt';
  /** Forced-victim duration. */
  durationMs: Ms;
}

export interface ThreatEffect extends EffectBase {
  type: 'threat';
  /** Flat threat added to the target's table for the source. */
  amount?: number;
  /** Multiply source's existing threat on target (e.g. 0 to drop, 0.5 to halve). */
  multiply?: number;
}

export interface ResourceEffect extends EffectBase {
  type: 'resource';
  resource: ResourceId;
  /** Positive = gain, negative = drain. */
  amount: number;
}

export interface KnockbackEffect extends EffectBase {
  type: 'knockback';
  /** Push away from (positive) or pull toward (negative) the source. */
  distance: Yards;
  /** Duration of the displacement (unit cannot act). */
  durationMs: Ms;
}

/** Pull target to the caster (Grip). */
export interface GripEffect extends EffectBase {
  type: 'grip';
  /** Stop this far in front of the caster. */
  stopAt: Yards;
}

export interface SummonEffect extends EffectBase {
  type: 'summon';
  npcId: NpcId;
  count: number;
  /** Spawn around the anchor in a ring of this radius. */
  radius: Yards;
  anchor: AreaAnchor;
  /** Summons join the caster's pack and enter combat immediately (default true). */
  joinCombat?: boolean;
  /**
   * +acolyte-specs (S7, combat/pets.ts): a friendly pet instead of an enemy add. The unit joins the
   * caster's side (faction 'party', no AI, no threat table, never takes damage), follows its owner,
   * attacks the owner's target with `attackAbilityId` (owner-sourced: the owner's damage, threat and
   * curses) every `intervalMs`, and despawns after `durationMs` or when the owner dies.
   */
  pet?: PetSpec;
  /**
   * +ranger-specs (S7 companions, combat/pets.ts, persistent pets): a persistent fighting companion (the
   * Beastbound's spirit beast) instead of an enemy add: a real unit on the caster's side with its own
   * health, stats (a share of its owner's), swings, threat and death; its behaviour is the NpcDef's
   * `companion`. Replaces the caster's earlier companion.
   */
  companion?: CompanionSummon;
}

/** +ranger-specs (S7 companions): how a `summon` places a companion. */
export interface CompanionSummon {
  /** Evolving forms by the owner's level: the highest `minLevel` ≤ the owner's level wins (else `npcId`). */
  forms?: { minLevel: number; npcId: NpcId }[];
  /**
   * Upkeep: do nothing while the owner has a living companion, or carries its def's `reformAuraId`
   * (a passive re-summons on a timer).
   */
  ensure?: boolean;
}

/** +ranger-specs (S7 companions): a companion's behaviour (on its NpcDef; every form carries one). */
export interface CompanionDef {
  /** Share of the owner's effective power and max health it fights with (re-synced every tick). */
  powerPct: number;
  hpPct: number;
  /** Its auto-attack (its own power; `perStack` on 'target' reads marks). */
  melee: { damage: Amount; speedMs: Ms };
  /**
   * Fighting as one: a hit by the owner and one by the companion on the same target within `windowMs`
   * gives the owner `amount` of `resource` (at most once per `windowMs`). Periodic damage and
   * `excludeAbilityIds` never count.
   */
  sync?: { resource: ResourceId; amount: number; windowMs: Ms; excludeAbilityIds?: AbilityId[] };
  /**
   * While its OWNER carries this aura it guards: it stays at the owner's side and takes what attacks
   * the owner (the command lives on the owner, so it survives the beast dying and re-forming).
   */
  guardAuraId?: AuraId;
  /** Kept on the companion while it guards (threat, toughness), removed while it hunts. */
  guardAura?: AuraDef;
  /** A guarding companion uses this on an enemy attacking its owner (a taunt), every `guardEveryMs`. */
  guardAbilityId?: AbilityId;
  guardEveryMs?: Ms;
  /** Auras it carries from the moment it forms (its stance, passives). */
  auras?: AuraDef[];
  /** Health regained per second out of combat (fraction of max). */
  regenPct?: number;
  /** Move speed relative to its owner's (it runs a little faster to keep up). */
  speedMult?: number;
  /**
   * When it dies its owner gets this aura for `reformMs` (give it `removeOutOfCombat`): the "re-forming"
   * timer an `ensure` summon waits out.
   */
  reformAuraId?: AuraId;
  reformMs?: Ms;
}

/** +ranger-specs (S7 companions): live state on a companion unit (Unit.companion). */
export interface CompanionState {
  ownerId: UnitId;
  /** The owner's and the companion's last counted hits (sync bookkeeping). */
  ownerHitOn: UnitId | null;
  ownerHitAt: Ms;
  hitOn: UnitId | null;
  hitAt: Ms;
  lastSyncAt: Ms;
  /** Next guard taunt. */
  guardAt: Ms;
  /** Its death was handled (the owner's re-forming timer started). */
  fallen?: boolean;
}

/** +acolyte-specs (S7): a summoned friendly pet (Heretic's Hollow One). */
export interface PetSpec {
  durationMs: Ms;
  /** Ability whose effects the pet's attack runs (source = owner, target = the pet's target, point = the pet). */
  attackAbilityId: AbilityId;
  /** Attack interval before the owner's haste. */
  intervalMs: Ms;
}

/** +acolyte-specs (S7): live pet state on a summoned unit (Unit.pet). */
export interface PetState {
  ownerId: UnitId;
  expiresAt: Ms;
  attackAbilityId: AbilityId;
  intervalMs: Ms;
  nextAttackAt: Ms;
}

/** Delayed area attack drawn on the ground. Resolves `effects` on units inside at `delayMs`. */
export interface TelegraphEffect extends EffectBase {
  type: 'telegraph';
  shape: AreaShape;
  anchor: AreaAnchor;
  delayMs: Ms;
  affects: Affects;
  /** Shape follows the anchor unit/facing until it resolves (default: snapshot). */
  follow?: boolean;
  /** Applied to every unit inside the shape at resolution (source = caster). */
  effects: EffectSpec[];
  /** Optional repeats (e.g. pulsing pools): resolve again every intervalMs, `repeats` more times. */
  repeat?: { intervalMs: Ms; repeats: number };
}

/** Persistent ground area (Consecrated Ground, Earthquake, poison pools). */
export interface ZoneEffect extends EffectBase {
  type: 'zone';
  zoneId: string; // definition id used for de-duping / rendering ("warden.consecrated_ground")
  shape: AreaShape;
  anchor: AreaAnchor;
  durationMs: Ms;
  affects: Affects;
  /** Effects applied to each unit inside every intervalMs (first tick at intervalMs). */
  tick?: { intervalMs: Ms; effects: EffectSpec[]; hasteScaled?: boolean };
  /** Aura kept on units while they stand inside (removed on exit). */
  insideAura?: AuraDef;
  /** Only one per caster with this zoneId (recast replaces). Default true. */
  uniquePerCaster?: boolean;
  /**
   * +basekits: a trap. The first tick with anyone inside resolves on the nearest occupant only, then
   * the zone ends (Snare Coil).
   */
  triggerOnce?: boolean;
  /**
   * +basekits: the zone travels (a slow projectile area: Wandering Star). Only 'linear' is used by
   * player abilities; 'target' heads for the caster's target (else the cast point, else facing).
   */
  motion?: ZoneMotionSpec;
  /** +basekits: effects resolved once where the zone is when it runs out (source = caster, point = its centre). */
  onExpire?: EffectSpec[];
  /**
   * +lead: rotation rework (F9). Capture the caster's power / crit / damage and healing multipliers
   * (including mods scoped to the zone's abilityId) at creation; ticks use the snapshot.
   */
  snapshot?: boolean;
}

export interface DashEffect extends EffectBase {
  type: 'dash';
  /**
   * 'toTarget' = charge to melee range of target; 'toPoint' = blink to point; 'forward' = along facing.
   * +classes: 'backward' = leap back against the facing (facing kept); 'behindTarget' = step to the
   * target's back (facing it).
   */
  mode: 'toTarget' | 'toPoint' | 'forward' | 'backward' | 'behindTarget';
  distance?: Yards; // for 'forward', and max distance for others
  /** 0 = instant teleport. Otherwise yards/sec during the dash. */
  speed?: number;
}

export interface ModifyCooldownEffect extends EffectBase {
  type: 'modifyCooldown';
  abilityId: AbilityId;
  /** Negative reduces remaining cooldown; 'reset' sets it ready (all charges). */
  deltaMs?: Ms;
  reset?: boolean;
  /** Grant one charge. */
  addCharge?: boolean;
}

/** Branch on a condition. */
export interface ConditionalEffect extends EffectBase {
  type: 'conditional';
  cond: Condition;
  then: EffectSpec[];
  else?: EffectSpec[];
}

/** Instant kill / set HP (encounter scripting, splits). */
export interface SetHpEffect extends EffectBase {
  type: 'setHp';
  pctMaxHp: number;
}

/**
 * +ranger-specs (S7 companions, combat/pets.ts, persistent pets): the caster's companion uses `abilityId` right
 * now (its own cast: source = the companion, so its damage, threat and the Bond sync are its own) on
 * the effect's hostile target, else on its current target. `leap` first bounds it into melee range
 * of that target; `toOwner` instead bounds it to where the caster stands (before a dash moves it).
 * No companion (dead, re-forming): nothing happens.
 */
export interface CompanionCastEffect extends EffectBase {
  type: 'companionCast';
  abilityId: AbilityId;
  leap?: boolean;
  toOwner?: boolean;
}

export type EffectSpec =
  | DamageEffect
  | HealEffect
  | AbsorbEffect
  | ApplyAuraEffect
  | RemoveAuraEffect
  | InterruptEffect
  | DispelEffect
  | TauntEffect
  | ThreatEffect
  | ResourceEffect
  | KnockbackEffect
  | GripEffect
  | SummonEffect
  | TelegraphEffect
  | ZoneEffect
  | DashEffect
  | ModifyCooldownEffect
  | ConditionalEffect
  | SetHpEffect
  | MechanicEffect // +encounters
  | CompanionCastEffect; // +ranger-specs (S7 companions)

export type EffectType = EffectSpec['type'];

// ============================================================================
// 8. Auras
// ============================================================================

export type AuraStacking =
  /** One instance per caster+target; reapply refreshes duration (with pandemic if set). */
  | 'refresh'
  /** One instance per caster+target; reapply adds stacks up to maxStacks and refreshes. */
  | 'stack'
  /** One instance per target regardless of caster (latest wins). */
  | 'unique'
  /** Reapply does nothing while active. */
  | 'ignore';

/** Event hooks that fire effects while an aura is active. */
export interface ProcSpec {
  on:
    | 'cast' // holder completes a cast (or instant) — abilityIds filter
    | 'damageDealt'
    | 'damageTaken'
    | 'healDealt'
    | 'critDealt'
    | 'meleeSwing'
    | 'auraExpire' // this aura expires naturally
    | 'auraRemoved' // removed for any reason
    /**
     * +lead: rotation rework (F3). Once per completed cast, after its effects resolved on every target
     * (channels: after channelStartEffects). Same-cast guard: never fires on an aura instance applied
     * at or after the resolving cast's start. consumeStack procs run before the other castResolved procs.
     */
    | 'castResolved'
    /** +lead: rotation rework (F6). The holder's auto-attack hit, crit or was blocked (not on miss/dodge/parry). */
    | 'meleeSwingLanded'
    /** +lead: rotation rework (F6). The holder avoided an avoidable attack (filter: hitTypes, default dodge/parry/block). */
    | 'avoided'
    /** +lead: rotation rework (F6). The holder's interrupt effect stopped a cast. */
    | 'interruptDealt'
    /** +lead: rotation rework (F14). The holder's heal crit. */
    | 'critHealed'
    /**
     * +basekits: an enemy NPC died with the holder on its threat table (kill credit, like XP). Fires
     * on every credited party member, other party = the dead unit (Bloodprize's window).
     */
    | 'killCredit';
  abilityIds?: AbilityId[];
  chance?: number; // default 1
  /** Internal cooldown between procs. */
  icdMs?: Ms;
  /** Effects run with source = aura holder, target = event's other party (or holder). */
  effects: EffectSpec[];
  /** Remove one stack (or the whole aura when stacks hit 0) after proccing. */
  consumeStack?: boolean;
  /**
   * +lead: rotation rework (F4). Bad-luck protection: effective chance = min(max ?? 1, chance + step × misses).
   * Still one ctx.rng roll; the miss counter (AuraInstance.procMiss) resets on success. Events blocked
   * by the ICD or by `if` don't count as misses.
   */
  badLuck?: { step: number; max?: number };
  /** +lead: rotation rework (F5). Gate checked before the ICD and the roll (source = holder, target = event's other party). */
  if?: Condition;
  /** +lead: rotation rework (F6). Hit types that count for 'avoided' (default dodge | parry | block). */
  hitTypes?: HitType[];
}

/** +lead: rotation rework (F2). While the aura is on its holder, a cast of `abilityId` casts `with` instead. */
export interface AbilityOverride {
  abilityId: AbilityId;
  with: AbilityId;
}

/** +lead: rotation rework (F11). HUD feedback metadata on an aura. */
export interface AuraUi {
  /** Counts as a proc: HUD flash and harness rhythm metrics. */
  proc?: boolean;
  /**
   * Spell-activation glow. Explicit ability list + stack threshold (default 1), or false to suppress
   * the automatic glow derived from ability-scoped mods of a `proc` aura. Debuffs glow from the
   * caster's instance on its current target.
   */
  glow?: { abilityIds: AbilityId[]; atStacks?: number } | false;
  /** Show this aura's stack count on these buttons (debuffs: the caster's instance on the current target). */
  stackCounterOn?: AbilityId[];
}

export interface AuraDef {
  id: AuraId;
  name: string;
  /** Buff (helpful) or debuff (harmful). Drives applybuff vs applydebuff events. */
  kind: 'buff' | 'debuff';
  /** Duration; 0 = until removed. Haste never scales duration. */
  durationMs: Ms;
  stacking: AuraStacking;
  maxStacks?: number;
  /** Pandemic: new duration = min(new + remaining, 1.3 × new). */
  pandemic?: boolean;
  dispelType?: DispelType;
  /** Periodic effect (DoT/HoT/pulse). Ticks while accumulated time >= interval. */
  periodic?: {
    intervalMs: Ms;
    effects: EffectSpec[];
    /** Interval divided by (1 + haste) of the caster at application. */
    hasteScaled?: boolean;
    /** Partial final tick on expiry (WoW-style). Default false. */
    partialFinalTick?: boolean;
  };
  /** Stat modifiers while active (multiplied by stacks for additive kinds). */
  mods?: StatMod[];
  /** Crowd control category (enables DR, ControlState). */
  cc?: CcType;
  /** This aura holds an absorb shield; amount stored on the instance. */
  absorb?: { schools?: School[] };
  flags?: {
    immuneDamage?: boolean;
    immuneCc?: boolean;
    /** Breaks on any damage taken (incapacitate-style). */
    breakOnDamage?: boolean;
    /** Persists through death (rare). */
    persistThroughDeath?: boolean;
    /** Hidden from UI. */
    hidden?: boolean;
    /** Removed when the holder leaves combat. */
    removeOutOfCombat?: boolean;
    /** +encounters: immune to mechanic pulls/knockbacks (Blessing of Freedom). */
    immuneDisplacement?: boolean;
    /** +basekits: removed when the holder enters combat (out-of-combat toggles: Swiftfoot, Lurk). */
    removeInCombat?: boolean;
    /** +specs: the holder's health cannot drop below 1 (Crimson Trance). */
    cannotDie?: boolean;
    /** +specs: the holder ignores movement slows (negative moveSpeedPct); roots still hold (Unstoppable). */
    immuneSlow?: boolean;
    /**
     * +mage-specs (S9, combat/cheatDeath.ts): once per fight, damage that would kill the holder leaves
     * it at 1 HP instead and runs `AuraDef.onCheatDeath` (Cinderborn's Ashrise). The instance is spent
     * until the holder leaves combat (or the aura is applied afresh).
     */
    cheatDeath?: boolean;
    /**
     * +basekits: stealth (Lurk). NPCs don't notice the holder by proximity beyond
     * STEALTH_DETECT_RADIUS; the renderer fades the unit.
     */
    stealth?: boolean;
  };
  procs?: ProcSpec[];
  /** Effects when applied / when it ends (any reason). */
  onApply?: EffectSpec[];
  onRemove?: EffectSpec[];
  /** +mage-specs (S9): effects when `flags.cheatDeath` saves the holder (source = target = the holder). */
  onCheatDeath?: EffectSpec[];
  /** +encounters: healing absorb. The amount lives in AuraInstance.absorbRemaining; heals fill it first. */
  healAbsorb?: boolean;
  /** +encounters: explodes on natural expiry (ai/mechanics). */
  explodeOnExpire?: ExplodeOnExpireSpec;
  /** +encounters: jumps to the nearest ally when dispelled (ai/mechanics). */
  jumpOnDispel?: JumpOnDispelSpec;
  /** +encounters: bot/renderer hint: the holder is dangerous to stand near. */
  hazard?: { radius: Yards; intent: 'spread' | 'isolate'; growsToExpiry?: boolean };
  /** +lead: rotation rework (F2). While on the holder, a cast of `abilityId` casts `with` instead (latest applied wins). */
  overrides?: AbilityOverride[];
  /** +lead: rotation rework (F11). HUD feedback (proc flag, glow, stack counters). */
  ui?: AuraUi;
  /**
   * +rogue-specs (combat/clones.ts): the holder's completed casts of the listed abilities are echoed
   * once per stack of this aura, a beat later (Shadowdancer's Silhouettes repeat Umbral Finale).
   */
  echoes?: EchoSpec[];
  /**
   * +acolyte-specs (S6, combat/absolution.ts): damage-to-healing transfer. When the holder damages a
   * hostile, `pct` of the damage (after mitigation) heals every ally within `radius` that carries the
   * holder's `markAuraId` (Lightbinder's Absolution); with more than `shareAbove` marked allies the
   * total is split evenly among them. Logged as heals of `markAuraId`.
   */
  transfer?: TransferSpec;
  /**
   * +acolyte-specs (S10, combat/martyr.ts): damage share. `pct` of the damage (after mitigation,
   * before absorbs) that hostiles deal to allies within `radius` of the holder is dealt to the holder
   * instead (raw, never lethal). When the aura ends the holder heals those allies for
   * `healBackPct` × everything it took, split evenly (Hemomancer's Martyr).
   */
  damageShare?: DamageShareSpec;
  /**
   * +ranger-specs (combat/tally.ts): while an instance of this aura lives on a unit it counts one stack
   * of the `tally` aura (from the same caster) on that unit: the tally's stacks always equal the
   * number of tallying instances (capped at its maxStacks) and it goes when the last one does
   * (Grovewarden's Thicket counts its heals-over-time).
   */
  tally?: AuraId;
}

/** +acolyte-specs (S6): see AuraDef.transfer. */
export interface TransferSpec {
  pct: number;
  markAuraId: AuraId;
  schools?: School[];
  radius?: Yards;
  shareAbove?: number;
}

/** +acolyte-specs (S10): see AuraDef.damageShare. */
export interface DamageShareSpec {
  pct: number;
  radius: Yards;
  healBackPct?: number;
}

/**
 * +rogue-specs (combat/clones.ts). One echo rule on an aura: after the holder completes a cast of one
 * of `abilityIds`, every stack of the aura re-runs that ability's damage effects (conditionals
 * re-evaluated, everything else skipped) on the same target, `delayMs` + i × `stepMs` later, scaled
 * by `mult`, logged under `echoAbilityId` (so aura mods scoped to it apply: a 100% echo window).
 */
export interface EchoSpec {
  abilityIds: AbilityId[];
  mult: number;
  echoAbilityId: AbilityId;
  delayMs: Ms;
  /** Gap between consecutive echoes (default 150). */
  stepMs?: Ms;
  /** Spend the aura when it echoes (default true). */
  consume?: boolean;
}

/** +rogue-specs (combat/clones.ts): an echo waiting to land. */
export interface PendingEcho {
  at: Ms;
  sourceId: UnitId;
  targetId: UnitId | null;
  point: Vec2 | null;
  /** The echoed ability (its damage effects are re-run). */
  abilityId: AbilityId;
  echoAbilityId: AbilityId;
  mult: number;
}

/** A live aura on a unit. */
export interface AuraInstance {
  iid: AuraInstanceId;
  auraId: AuraId;
  casterId: UnitId;
  /** Ability that applied it (log attribution). */
  abilityId: AbilityId;
  appliedAt: Ms;
  /** Absolute expiry; null = permanent. */
  expiresAt: Ms | null;
  /** Full duration of the current application (for UI and pandemic). */
  durationMs: Ms;
  stacks: number;
  /** Periodic bookkeeping. */
  periodicIntervalMs?: Ms;
  nextTickAt?: Ms;
  /** Remaining absorb (absorb auras only). */
  absorbRemaining?: number;
  /** Last proc time per ProcSpec index (ICD). */
  procIcd?: Record<number, Ms>;
  /** +lead: rotation rework (F4). Consecutive failed rolls per ProcSpec index (bad-luck protection). */
  procMiss?: Record<number, number>;
  /** Snapshot of caster stats needed for periodic math (power, crit, damageDoneMult). */
  snapshot?: { power: number; crit: number; damageDoneMult: number; healingDoneMult: number };
  /** +mage-specs (S9): this cheatDeath instance already saved its holder this fight. */
  cheatDeathSpent?: boolean;
  /** +acolyte-specs (S10): damage this damageShare instance has taken for its allies so far. */
  shared?: number;
}

// ============================================================================
// 9. Abilities (castables for players AND NPCs)
// ============================================================================

export type CastKind =
  | { type: 'instant' }
  | { type: 'cast'; timeMs: Ms }
  | { type: 'channel'; durationMs: Ms; ticks: number };

export interface CooldownSpec {
  durationMs: Ms;
  /** Charges (default 1). Recharge one at a time. */
  charges?: number;
  /** Cooldown recovery is haste-scaled (rare; default false). */
  hasteScaled?: boolean;
}

export interface AbilityDef {
  id: AbilityId;
  name: string;
  school: School;
  /** Short tooltip for the HUD. */
  description?: string;
  /** Resource costs, paid on cast completion (instant: on use). */
  cost?: ResourceAmount[];
  /** Resource gains on cast completion. */
  gain?: ResourceAmount[];
  cooldown?: CooldownSpec;
  /** GCD in ms before haste (default BASE_GCD_MS). 0 = off the GCD. */
  gcdMs?: Ms;
  /** Default { type: 'instant' }. */
  cast?: CastKind;
  /** Max range in yards (edge to edge, combat reach added). 0 or missing with targeting 'enemy' = melee range. */
  range?: Yards;
  /** Explicit melee-range requirement (uses melee range formula). */
  melee?: boolean;
  /**
   * +autoattack: using this ability starts the caster's auto-attack (melee swings or Auto Shot).
   * Default: any hostile weapon ability of a class with a melee or ranged weapon (melee, an enemy
   * target with damage, or self-centred damage on enemies); casters' spells never start it.
   */
  startsAttack?: boolean;
  targeting: Targeting;
  /** Target must be in front of the caster (180° arc). */
  requiresFacing?: boolean;
  /** Can start/continue casting while moving (instant abilities always can). */
  canCastWhileMoving?: boolean;
  /** Off-GCD instant usable during the caster's own hard cast without cancelling it (default false). */
  usableWhileCasting?: boolean;
  /** Needs the caster free to move (dodge): fails with 'rooted' while rooted. */
  requiresMobility?: boolean;
  /** Cast/channel can be interrupted. Players' casts: true. NPC casts set explicitly. */
  interruptible?: boolean;
  /** Usable only when this holds (Execute: target < 20% HP). */
  usableIf?: Condition;
  /** Resolved on cast completion (instant: on use). Channels: applied on each tick. */
  effects: EffectSpec[];
  /** Effects applied once at channel start (e.g. Bladestorm self-buff). */
  channelStartEffects?: EffectSpec[];
  /**
   * +lead: rotation rework (F10). Effects run once when the channel ends (isCast false, source = caster,
   * target = the channel's target). 'complete' (default): only when it runs to its end. 'any': also
   * when cancelled or interrupted (not on the caster's death).
   */
  channelEndEffects?: EffectSpec[];
  channelEndOn?: 'complete' | 'any';
  /**
   * +mage-specs (S11): the cast empties `resource` (needs at least `min`), paid with the other costs.
   * The points taken reach the effects as `EffectCtx.spent` (Amount.perSpent, AreaSpec.perSpentTargets).
   */
  spendAll?: { resource: ResourceId; min: number };
  /** Threat generated per cast in addition to damage threat (and for 0-damage utility). */
  flatThreat?: number;
  /** Purely cosmetic hints for renderers/HUD. */
  ui?: { icon?: string; color?: string; tags?: AbilityTag[]; /** +lead: rotation rework (F2): renderer clip fallback until this ability has its own clip. */ animAs?: AbilityId; /** +tooltips: hand-written tooltip text replacing the generated effect paragraph; `{damage}`, `{damage2}`, `{heal}`, `{absorb}` insert live amounts. */ desc?: string; /** +basekits: one-line "how to use" for the level-up banner. */ hint?: string };
}

export type AbilityTag =
  | 'interrupt'
  | 'dispel'
  | 'defensive'
  | 'majorDefensive'
  | 'externalDefensive'
  | 'taunt'
  | 'aoe'
  | 'builder'
  | 'spender'
  | 'heal'
  | 'groupHeal'
  | 'movement'
  | 'cooldown'
  | 'execute';

/** Hotbar keys: 4 rotation, 2 cooldowns, 2 utility (PLAN §4). */
export type BindKey = '1' | '2' | '3' | '4' | 'Q' | 'E' | 'R' | 'F';
export const BIND_KEYS: readonly BindKey[] = ['1', '2', '3', '4', 'Q', 'E', 'R', 'F'];
/** +lead: rotation rework (F1). Keys added by the rework; bound through the optional `ClassDef.extraBinds`. */
export type ExtraBindKey = '5' | '6' | '7' | 'G';
/** +basekits: '7' carries a base kit's level-7 unlock. */
export const EXTRA_BIND_KEYS: readonly ExtraBindKey[] = ['5', '6', '7', 'G'];
/** +lead: rotation rework (F1). Every hotbar key in display / iteration order. */
export type HotbarKey = BindKey | ExtraBindKey;
export const HOTBAR_KEYS: readonly HotbarKey[] = ['1', '2', '3', '4', '5', '6', '7', 'Q', 'E', 'R', 'F', 'G'];

// ============================================================================
// 10. Classes
// ============================================================================

export interface ClassDef {
  id: ClassId;
  name: string;
  role: Role;
  /** Resources this class has (first = primary, shown as the main bar). */
  resources: ResourceDef[];
  /**
   * One ability per key. Extra abilities (e.g. a second F action) go in `extraAbilities`.
   * +classes: the five base classes bind only their five-slot kit, so keys may be empty.
   */
  binds: Partial<Record<BindKey, AbilityId>>;
  /** Abilities this class owns (every bind id must be in here). */
  abilities: AbilityDef[];
  /** Abilities usable but not bound (alt-binds, procs). */
  extraAbilities?: AbilityId[];
  /** +lead: rotation rework (F1). Binds for keys 5, 6 and G. Every id must also be in `abilities`. */
  extraBinds?: Partial<Record<ExtraBindKey, AbilityId>>;
  baseStats: Stats;
  maxHp: number;
  moveSpeed: Yards; // yards/sec (WoW run speed = 7)
  /**
   * Melee auto-attack. Casters set null (no swings). +autoattack: `offhand` = dual wield (an
   * independent off-hand timer, `mult` of the main-hand damage, DUAL_WIELD_MISS on white swings).
   */
  melee: { damage: Amount; speedMs: Ms; school?: School; offhand?: { speedMs: Ms; mult: number } } | null;
  /**
   * +autoattack: the weapon of a class without `melee`: 'ranged' = Auto Shot (a bow, started by its
   * shots and by right-click at range); 'staff' = a caster's melee bonk (right-click / Attack in
   * melee range only, never from spells).
   */
  weapon?: { kind: 'ranged' | 'staff'; damage: Amount; speedMs: Ms; school?: School; range?: Yards };
  /** Permanent passive auras (e.g. tank threat aura, mastery). */
  passives?: AuraDef[];
  /** Placeholder color for renderers. */
  color: string;
  /**
   * +field: ability id → hero level that unlocks it (docs/design/zones.md). Casting refuses a locked
   * ability ('locked') for a player unit whose `level` is below it. Units without a level (M+ keys)
   * have everything.
   */
  unlocks?: Partial<Record<AbilityId, number>>;
  /**
   * +specs: set on a ClassDef produced by `applySpec` (the base kit with a specialisation folded in).
   * The id stays the base class id for the profile hero (one hero per sim); Group Finder strangers and
   * the harness may register a spec'd def under `specClassId(base, spec)` ("warrior@ironclad").
   */
  specId?: SpecId;
  /** +specs: the base class a spec'd def came from (always set together with `specId`). */
  baseClassId?: ClassId;
}

// ============================================================================
// 10b. Specialisations (+specs, docs/design/classes/specs.md)
// ============================================================================

export type SpecId = string;

/**
 * A level-10 specialisation of a base class. `applySpec(base, spec)` folds it into a ClassDef:
 * every base ability in `evolutions` is replaced by its evolved form on the same key (binds,
 * extraBinds, extraAbilities and references in kept passives), the spec-only abilities are bound to
 * their keys, `resources` (when given) replace the base resources, `passives` are added (after
 * `removePassives` are dropped) and the role, stats and weapon may change. Pure and deterministic.
 */
export interface SpecDef {
  id: SpecId;
  baseClassId: ClassId;
  name: string;
  role: Role;
  /** Hero level the spec is chosen at (the Tower Warden; 10 in phase 1). */
  unlockLevel: number;
  /** Base ability id → the spec's ability id (same key, same muscle memory). Every bound base ability must be mapped. */
  evolutions: Record<AbilityId, AbilityId>;
  /** Spec-only abilities and the hotbar key each binds to (Q / E / R / F / G). */
  extraAbilities: { key: HotbarKey; abilityId: AbilityId }[];
  /** Every AbilityDef the spec adds: evolved forms, spec-only abilities and their transform forms. */
  abilities: AbilityDef[];
  /** Replaces the base class's resources when present (first = primary bar, second = pips). */
  resources?: ResourceDef[];
  /** Passive auras added to the class's (signature mechanic, role auras). */
  passives?: AuraDef[];
  /** Base passive ids the spec drops (a dps spec drops the tank threat aura). */
  removePassives?: AuraId[];
  /** Standalone auras referenced only by id (absorb shields, proc-only buffs). */
  auras?: AuraDef[];
  /** Partial overrides of the base stats (e.g. a dps Warrior loses the shield's block). */
  baseStats?: Partial<Stats>;
  /** Max HP override (before gear and level scaling). */
  maxHp?: number;
  melee?: ClassDef['melee'];
  weapon?: ClassDef['weapon'];
  color?: string;
  /** Presentation (Advancement Trial cards, character sheet, renderer tint). Never read by the sim. */
  ui?: SpecUi;
}

export interface SpecUi {
  /** One-line hook. */
  tagline: string;
  /** Two or three sentences of fantasy. */
  fantasy: string;
  /** The signature mechanic in one sentence. */
  signature: string;
  /** Weapon the hero model carries ('tower_shield', 'twin_greatswords', 'red_greatsword', 'revolver', …). */
  weapon: string;
  /** Weapon / aura tint (CSS colour). */
  tint: string;
  /** Accent glow colour (CSS colour). */
  glow: string;
}

/** +specs: registry key of a spec'd ClassDef registered beside its base ("warrior@ironclad"). */
export const specClassId = (baseClassId: ClassId, specId: SpecId): ClassId => `${baseClassId}@${specId}`;
/** +specs: the base class of a (possibly spec-keyed) class id: "warrior@ironclad" → "warrior". */
export const baseClassIdOf = (classId: ClassId): ClassId => {
  const i = classId.indexOf('@');
  return i < 0 ? classId : classId.slice(0, i);
};

// ============================================================================
// 11. Enemies & encounters (PLAN §5)
// ============================================================================

export type EnemyAbilityKind =
  | 'bolt'
  | 'tankbuster'
  | 'frontal'
  | 'groundAoE'
  | 'pulse'
  | 'cc'
  | 'heal'
  | 'shield'
  | 'summon'
  | 'charge'
  | 'buff';

/** How the AI picks the target for an enemy ability. */
export type EnemyTargetRule =
  | 'victim' // current threat victim (usually tank)
  | 'tank'
  | 'random' // random party member (seeded)
  | 'randomNotTank'
  | 'farthest'
  | 'nearest'
  | 'healer'
  | 'self'
  | 'lowestHpAlly' // for NPC heals: lowest HP% ally NPC in range
  | 'allies' // ally NPCs (pack buff)
  | 'none';

/** EventMap-style timer (bosses) / SmartAI ranges (trash). All ms. */
export interface EnemyTimer {
  /** First cast this long after entering combat (or phase start). */
  first: Ms;
  /** Repeat interval measured from *cast start* (EventMap Repeat counts from now). */
  every: Ms;
  /** Uniform ± jitter applied to each scheduling (seeded). */
  jitter?: Ms;
  /** +integrator: SmartAI ranges. When set, first is rolled in [first..firstMax], repeat in [every..everyMax]. */
  firstMax?: Ms;
  everyMax?: Ms;
  /** +encounters: cast chain: after the first cast, `count - 1` more casts follow `gapMs` after each cast ends. */
  chain?: { count: KeyScaled; gapMs: Ms };
}

/**
 * An NPC ability: a normal AbilityDef (cast time, interruptible, effects...) plus the
 * AI scheduling data. Registered in ContentRegistry.abilities under its id like any ability.
 */
export interface EnemyAbility extends AbilityDef {
  kind: EnemyAbilityKind;
  timer: EnemyTimer;
  target: EnemyTargetRule;
  /** Informational: debuff from this ability is dispellable by this type. */
  dispellable?: DispelType;
  /** Telegraph/shape hint for AI facing and bot awareness (actual hit area lives in effects). */
  shape?: AreaShape;
  /** Higher fires first when several timers are due on the same update (EventMap: one per update). */
  priority?: number;
  /** Only used while caster HP fraction is in [minHp, maxHp]. */
  hpWindow?: { min?: number; max?: number };
  /** +encounters: replaces hpWindow while the affix is active (Fortified frenzies at 40%). */
  hpWindowAffix?: Partial<Record<AffixId, { min?: number; max?: number }>>;
  /** +encounters: AI only uses it at keystone level >= this (and <= maxKeyLevel). */
  minKeyLevel?: number;
  maxKeyLevel?: number;
  /** +encounters: AI only uses it while this affix is active. */
  requiresAffix?: AffixId;
  /** +encounters: cast at most once per engage (frenzies, HP-triggered rites). */
  once?: boolean;
  /** +encounters: the running cast is interruptible only while the caster lacks this aura (shield-gated). */
  interruptibleWhen?: { casterLacksAura: AuraId };
  /** +encounters: caster facing is frozen for the whole cast (Bloodroar's safe wedge). */
  lockFacing?: boolean;
  /** +encounters: bots: 1 = kick first. */
  kickPriority?: number;
  /** +encounters: bots/HUD counterplay hints. */
  counter?: CounterHint[];
  /** +encounters: HUD boss-frame / nameplate warning when the cast starts. */
  warning?: { text: string; severity: 'info' | 'danger' | 'deadly' };
}

export interface NpcDef {
  id: NpcId;
  name: string;
  tier: NpcTier;
  /** Relative to TIER_HP[tier] in the content package. */
  hpMult: number;
  /** Enemy forces value (1 / 5 / 7 / 25 / 30, PLAN §1). Bosses 0. */
  forces: number;
  /** Auto-attack; omit for casters that don't melee. */
  melee?: { dmg: number; speedMs: Ms; school?: School };
  /** Abilities, by id; their EnemyAbility defs are in the registry. Order = priority tie-break. */
  abilities: AbilityId[];
  radius: Yards;
  combatReach: Yards;
  moveSpeed: Yards;
  baseStats?: Partial<Stats>;
  /** Units that can be stunned/taunted etc. Bosses are typically ccImmune. */
  ccImmune?: boolean;
  /** Taunt DR applies (PLAN §3). Default: bosses true. */
  tauntDr?: boolean;
  /** Ranged casters keep this distance instead of running into melee. */
  preferredRange?: Yards;
  color: string;
  /** +encounters: passive auras applied when the NPC enters combat (removed by evade). */
  auras?: AuraDef[];
  /** +encounters: effects when it dies (source = the dead NPC, target = itself, point = its position). */
  onDeath?: EffectSpec[];
  /** +encounters: mirror `pct` of the damage it takes to its summoner / the encounter boss. */
  damageLink?: { to: 'summoner' | 'boss'; pct: number };
  /** +field: runs away from its attackers instead of fighting (the treasure sprite). */
  flees?: boolean;
  /** +specs: a practice dummy (the Tower Warden's): Auto and grind bots never hunt it. */
  practice?: boolean;
  /**
   * +tutorial: training-dummy reset. Evade (full HP, threat cleared, back to idle) once no hostile
   * has generated threat on it for this long. Checked every EVADE_CHECK_MS. Unset = never.
   */
  idleResetMs?: Ms;
  /** +ranger-specs (S7 companions): this NPC is a companion form (combat/pets.ts, persistent pets). */
  companion?: CompanionDef;
}

export interface Phase {
  /** Phase lasts until boss HP fraction drops below this (0..1). Missing = last phase. */
  untilHpPct?: number;
  /** Enemy ability ids active in this phase (timers restart from `first` on entry). */
  abilities: AbilityId[];
  /** Effects fired by the (first) boss on phase entry (source and target = boss). */
  onEnter?: EffectSpec[];
  /** +encounters: this phase is an intermission; it ends by its own rules, then the next phase starts. */
  intermission?: IntermissionDef;
  /** +encounters: per-ability first-cast delay (ms) on phase entry, overriding `timer.first` (repeats keep `timer.every`). */
  firstMs?: Record<AbilityId, Ms>;
}

export interface EncounterDef {
  id: EncounterId;
  name: string;
  /** NPC ids that make up the encounter (the pack with this encounterId spawns them). */
  bossNpcIds: NpcId[];
  phases: Phase[];
  /** Hard enrage after this long in combat (ms). */
  enrageMs?: Ms;
  /** Boss arena; the encounter seals while in progress (no leaving/joining). */
  arena?: Rect;
  /** +encounters: named arena points (braziers, carcass piles) used by soaks and intermission leaps. */
  markers?: Record<string, Vec2[]>;
}

// ----------------------------------------------------------------------------
// 11b. +encounters: boss/enemy mechanics (implemented in sim/src/ai/mechanics).
// Everything here is optional data; content that doesn't use it behaves as before.
// ----------------------------------------------------------------------------

/**
 * +encounters: a number that changes at keystone breakpoints and with affixes (retail +7/+10/+12).
 * Resolution: `base`, replaced by the `atKey` entry with the highest level <= the key, then
 * `affixMult` and `affixAdd` for every active affix.
 */
export type KeyScaled =
  | number
  | {
      base: number;
      atKey?: Record<number, number>;
      affixAdd?: Partial<Record<AffixId, number>>;
      affixMult?: Partial<Record<AffixId, number>>;
    };

/** +encounters: who a mechanic picks among the source's living enemies (the party). */
export type MechanicTargetRule =
  | 'primary' // the effect's target (falls back to the caster's victim)
  | 'victim' // the caster's threat victim
  | 'random'
  | 'randomNotTank'
  | 'farthest'
  | 'nearest'
  | 'tank'
  | 'healer'
  | 'all'
  | 'allNotTank';

/** +encounters: what bots/renderers should do about a telegraph or zone. */
export type HazardIntent = 'avoid' | 'deadly' | 'soak';

/** +encounters: counterplay hints on enemy abilities (bots, HUD tooltips). */
export type CounterHint =
  | 'interrupt'
  | 'dodge'
  | 'spread'
  | 'soak'
  | 'stack'
  | 'lookAway'
  | 'behindCaster'
  | 'kite'
  | 'burstShield'
  | 'defensive'
  | 'healerCd'
  | 'dispel'
  | 'runOut'
  | 'tankFacing'
  | 'killAdd';

/** +encounters: where a soak lands. */
export type SoakPlacement =
  | 'target'
  | 'caster'
  | 'point'
  /** Random spots on a ring around the caster/target (seeded). */
  | { around: 'caster' | 'target'; minDist: Yards; maxDist: Yards }
  /** One soak per encounter marker (EncounterDef.markers[marker]); lines aim at the caster when `aimAtCaster`. */
  | { marker: string; aimAtCaster?: boolean };

/** +encounters: how a hazard zone moves. Bots extrapolate with `Zone.motion.vel`. */
export type ZoneMotionSpec =
  /** Straight travel that reflects off the arena rect (or unwalkable ground). */
  | { type: 'bounce'; speed: number; heading?: 'facing' | 'target' | 'random' | 'spread' }
  /** Orbits the spawn point while drifting outward. */
  | { type: 'spiral'; angularSpeed: Radians; radialSpeed: number; startRadius?: Yards }
  /** Line/cone anchored on the caster that turns toward a target at a capped rate (tracking beam). */
  | { type: 'aim'; turnRate: Radians; rule?: MechanicTargetRule }
  /** Creeps toward a target unit. */
  | { type: 'chase'; speed: number; rule?: MechanicTargetRule }
  /** Circle radius grows (pools). `perOccupantSec`: extra growth per unit standing inside. */
  | { type: 'grow'; perSec: Yards; max: Yards; perOccupantSec?: Yards }
  /** Straight travel along the initial heading (waves of fire, rolling boulders). */
  | { type: 'linear'; speed: number; heading?: 'facing' | 'target' };

/** +encounters: serializable live motion state stored on `Zone.motion`. */
export interface ZoneMotionState {
  spec: ZoneMotionSpec;
  /** Current velocity in yards/sec (0,0 for aim/grow). */
  vel: Vec2;
  /** Spiral centre. */
  center?: Vec2;
  /** Spiral angle / aim heading. */
  angle?: Radians;
  /** Spiral radius. */
  dist?: Yards;
  /** Aim/chase target. */
  targetId?: UnitId | null;
  /** Aim: the unit the line is anchored on. */
  anchorId?: UnitId | null;
}

/** Pick N units and run effects on each (target = picked unit, point = its position). */
export interface PickMechanic {
  kind: 'pick';
  rule: MechanicTargetRule;
  /** Default 1. Ignored for 'all'/'allNotTank'. */
  count?: KeyScaled;
  /** Only units within this distance of the source (default 80). */
  range?: Yards;
  effects: EffectSpec[];
}

/** Shared soak: damage split among those inside; fails below `min`. */
export interface SoakMechanic {
  kind: 'soak';
  shape: AreaShape;
  at: SoakPlacement;
  /** Number of soaks (default 1; markers: up to the marker count). */
  count?: KeyScaled;
  /** Resolution delay ('timer') or lifetime ('enter'). */
  delayMs: Ms;
  /** Units needed inside (default 1). */
  min?: KeyScaled;
  /** 'timer' (default): resolve at delayMs. 'enter': resolve on the first eligible unit to step in; expire at delayMs. */
  trigger?: 'timer' | 'enter';
  /** Damage dealt to the units inside, split evenly (unless split === false). */
  damage?: { school: School; amount: Amount };
  split?: boolean;
  /** Per soaker (source = caster, target = soaker). */
  effects?: EffectSpec[];
  /** Once on failure (source = target = caster, point = soak centre). 'enter' soaks fail when they expire untouched. */
  failEffects?: EffectSpec[];
  /** Strip up to `stacks` stacks of an aura from each soaker (Bone Shards clear Ritual Venom). */
  consumes?: { auraId: AuraId; stacks: number };
  /** Only units holding this aura count as soakers / trigger 'enter' soaks. */
  eligible?: { hasAura?: AuraId };
  intent?: HazardIntent;
}

/** Continuous pull (speed > 0) or push (speed < 0) relative to the source while it lasts. */
export interface PullMechanic {
  kind: 'pull';
  durationMs: Ms;
  /** Yards per second. Positive pulls toward the centre, negative pushes away. */
  speed: number;
  /** Only units within this distance are affected (default 60). */
  radius?: Yards;
  /** Units stop being pulled this close to the centre (default 2). */
  minDist?: Yards;
  center?: 'caster' | 'point';
  /** Ends early when the caster stops casting the ability that started it (default true). */
  whileCasting?: boolean;
}

/** Knock the effect target back (or pull it in, distance < 0). Respects `immuneDisplacement`. */
export interface KnockbackMechanic {
  kind: 'knockback';
  distance: Yards;
  durationMs?: Ms;
  from?: 'caster' | 'point';
}

/** A ground hazard (ZoneEffect) that moves, aims or grows. */
export interface HazardMechanic {
  kind: 'hazard';
  zone: ZoneEffect;
  motion?: ZoneMotionSpec;
  /** Several copies (axes); default 1. */
  count?: KeyScaled;
  intent?: HazardIntent;
}

/** Tethers: each picked unit must get farther than `breakAt` from the source before it ends. */
export interface TetherMechanic {
  kind: 'tether';
  rule?: MechanicTargetRule;
  breakAt: Yards;
  durationMs: Ms;
  /** Each tether still holding at the end (source = caster, target = unit). */
  failEffects?: EffectSpec[];
  /** When a tether breaks (source = caster, target = unit, point = where it broke). */
  breakEffects?: EffectSpec[];
  /** Aura kept on the unit while tethered. */
  whileTethered?: AuraDef;
}

/** Telegraphed charge line: the caster dashes along it after `delayMs`. */
export interface ChargeMechanic {
  kind: 'charge';
  rule?: MechanicTargetRule;
  width: Yards;
  /** Fixed line length; default = distance to the target + overshoot. */
  length?: Yards;
  overshoot?: Yards;
  delayMs: Ms;
  /** The line keeps tracking the target for this long, then locks (default 0). */
  aimFollowMs?: Ms;
  /** Dash speed in yards/sec; 0 = teleport (default 45). */
  speed?: number;
  /** On every unit in the line when it goes off (source = caster, target = unit). */
  effects: EffectSpec[];
  /** Zone left along the path. Its shape is replaced by a rect covering the line. */
  trail?: ZoneEffect;
  intent?: HazardIntent;
}

/** Key-level / affix gate: per-key extra effects ("+7: also leaves a pool"). Same EffectCtx. */
export interface GateMechanic {
  kind: 'gate';
  minKey?: number;
  maxKey?: number;
  affix?: AffixId;
  notAffix?: AffixId;
  effects: EffectSpec[];
  elseEffects?: EffectSpec[];
}

/** Gaze: hits units that are looking at the source (turn your back to avoid). */
export interface GazeMechanic {
  kind: 'gaze';
  /** Facing arc that counts as "looking" (default 2π/3). */
  arc?: Radians;
  range?: Yards;
  effects: EffectSpec[];
}

/** Fixate: the caster (or its summoned `adds`) chases one player, ignoring threat. */
export interface FixateMechanic {
  kind: 'fixate';
  durationMs: Ms;
  rule?: MechanicTargetRule;
  /** Fixate the caster's living summons of this npc (each on a different player) instead of the caster. */
  adds?: NpcId;
  /** Marker debuff put on the fixated player (source = the fixating unit). */
  aura?: AuraDef;
}

/** Healing absorb on the effect target (aura must set `healAbsorb`). */
export interface HealAbsorbMechanic {
  kind: 'healAbsorb';
  aura: AuraDef;
  amount: Amount;
  /** Multiplier by key/affix (default 1). */
  scale?: KeyScaled;
}

/** Damage shield sized as a fraction of the holder's max HP (aura must set `absorb`). */
export interface ShieldMechanic {
  kind: 'shield';
  aura: AuraDef;
  pctMaxHp: KeyScaled;
  /** Default 'self' (the source). */
  on?: 'self' | 'target';
}

/** Staggered bands (ring or line) that go off one after another. */
export interface WaveMechanic {
  kind: 'wave';
  form: 'ring' | 'line';
  bands: KeyScaled;
  bandWidth: Yards;
  /** 'line' form: width across the line (default 8). */
  width?: Yards;
  /** Distance of the first band's inner edge from the anchor (default 0). */
  startAt?: Yards;
  anchor?: 'caster' | 'target' | 'point';
  delayMs: Ms;
  intervalMs: Ms;
  /** Applied to units inside a band when it goes off (source = caster). */
  effects: EffectSpec[];
  intent?: HazardIntent;
}

export type MechanicSpec =
  | PickMechanic
  | SoakMechanic
  | PullMechanic
  | KnockbackMechanic
  | HazardMechanic
  | TetherMechanic
  | ChargeMechanic
  | GateMechanic
  | GazeMechanic
  | FixateMechanic
  | HealAbsorbMechanic
  | ShieldMechanic
  | WaveMechanic;

/** +encounters: effect routed to ctx.sys.mechanics (combat runs it once per invocation). */
export interface MechanicEffect extends EffectBase {
  type: 'mechanic';
  spec: MechanicSpec;
}

/** +encounters: aura that explodes when it expires naturally (not when consumed/dispelled). */
export interface ExplodeOnExpireSpec {
  radius: Yards;
  /** Damage to the holder (unless hitsHolder === false) and its allies within radius. Source = aura caster. */
  damage?: { school: School; amount: Amount; perStack?: boolean };
  hitsHolder?: boolean;
  /** Extra effects on each unit hit (source = aura caster). */
  effects?: EffectSpec[];
  /** Zone left at the holder's feet (source = aura caster). */
  pool?: ZoneEffect;
  /** +encounters: the pool only spawns at this key level or higher. */
  poolMinKey?: number;
}

/** +encounters: aura that jumps to the nearest ally of the holder when dispelled. */
export interface JumpOnDispelSpec {
  radius: Yards;
  /** Effects on the new holder (source = aura caster). */
  effects?: EffectSpec[];
  /** New application gets the full duration (default: the remaining duration). */
  refresh?: boolean;
}

/** +encounters: HP-triggered intermission phase (Phase.intermission). */
export type IntermissionEnd =
  | 'addsDead'
  | { interrupted: AbilityId }
  | { castCompleted: AbilityId }
  | { auraGone: AuraId };

export interface IntermissionDef {
  /** Hard cap. On timeout `onTimeout` (and `perSurvivingAdd`) run and the next phase starts. */
  maxMs: KeyScaled;
  /** Any of these ends it early. */
  endsWhen?: IntermissionEnd[];
  /** Boss leaps here on entry (forced move over leapMs). */
  moveTo?: 'arenaCenter' | { marker: string; pick?: 'nearest' | 'farthest' | 'first' } | Vec2;
  leapMs?: Ms;
  /** Applied at the landing point when the leap ends (source = boss, point = landing). */
  landing?: EffectSpec[];
  /** Aura on every boss for the duration (immunity, root). Removed on exit. */
  bossAura?: AuraDef;
  /** Adds counted by 'addsDead' (default: every unit summoned by a boss). */
  addNpcIds?: NpcId[];
  /** When it ends early (source = target = boss). */
  onEnd?: EffectSpec[];
  /** On timeout (source = target = boss). */
  onTimeout?: EffectSpec[];
  /** On timeout, per surviving add (source = boss, target = add); the add is then despawned. */
  perSurvivingAdd?: EffectSpec[];
}

/** +encounters: live soak (its Telegraph shares the id). */
export interface SoakState {
  id: TelegraphId;
  sourceId: UnitId;
  abilityId: AbilityId;
  area: WorldShape;
  min: number;
  trigger: 'timer' | 'enter';
  resolvesAt: Ms;
  damage: { school: School; amount: Amount } | null;
  split: boolean;
  effects: EffectSpec[];
  failEffects: EffectSpec[];
  consumes: { auraId: AuraId; stacks: number } | null;
  eligibleAura: AuraId | null;
  /** Eligible units inside right now (UI pips). */
  inside: UnitId[];
}

export interface TetherState {
  id: string;
  sourceId: UnitId;
  unitId: UnitId;
  abilityId: AbilityId;
  breakAt: Yards;
  startedAt: Ms;
  endsAt: Ms;
  failEffects: EffectSpec[];
  breakEffects: EffectSpec[];
  auraId: AuraId | null;
}

export interface ChargeState {
  id: TelegraphId;
  sourceId: UnitId;
  abilityId: AbilityId;
  targetId: UnitId | null;
  resolvesAt: Ms;
  aimUntil: Ms;
  width: Yards;
  length: Yards | null;
  overshoot: Yards;
  speed: number;
  effects: EffectSpec[];
  trail: ZoneEffect | null;
  /** Caster position held during the wind-up. */
  hold: Vec2;
}

export interface PullState {
  id: string;
  sourceId: UnitId;
  abilityId: AbilityId;
  center: Vec2 | null;
  endsAt: Ms;
  speed: number;
  radius: Yards;
  minDist: Yards;
  /** Ends when the source stops casting this (null = runs to endsAt). */
  castAbilityId: AbilityId | null;
}

export interface DamageLinkState {
  unitId: UnitId;
  to: 'summoner' | 'boss';
  pct: number;
  lastHp: number;
}

export interface WatchedAuraState {
  iid: AuraInstanceId;
  unitId: UnitId;
  auraId: AuraId;
  casterId: UnitId;
  abilityId: AbilityId;
  stacks: number;
  expiresAt: Ms | null;
}

export interface IntermissionState {
  encounterId: EncounterId;
  phaseIndex: number;
  startedAt: Ms;
  endsAt: Ms;
  landingAt: Ms | null;
  landing: Vec2 | null;
  sawAdds: boolean;
  /** Boss casts being watched for interrupted/castCompleted ends. */
  casts: { unitId: UnitId; abilityId: AbilityId; endsAt: Ms }[];
  /** Auras seen on a boss for auraGone ends. */
  seenAuras: AuraId[];
}

/** +encounters: all live mechanic state (World.mechanics; created lazily). */
export interface MechanicsState {
  soaks: SoakState[];
  tethers: TetherState[];
  charges: ChargeState[];
  pulls: PullState[];
  links: DamageLinkState[];
  watched: WatchedAuraState[];
  /** Facing held while a lockFacing cast runs. */
  facingLocks: { unitId: UnitId; castStartedAt: Ms; facing: Radians }[];
  intermission: IntermissionState | null;
}

/** +encounters: implemented in sim/src/ai/mechanics; combat routes `mechanic` effects here. */
export interface MechanicsSystem {
  apply(ctx: SimContext, effect: MechanicEffect, ectx: EffectCtx): void;
  /** Per tick (called at the end of ai.update, before casting/zones/telegraphs). */
  update(ctx: SimContext): void;
}

// ============================================================================
// 12. Dungeon
// ============================================================================

export interface RoomDef {
  id: RoomId;
  name: string;
  /** Walkable area of the room. */
  shape: Polygon;
  /** +spatial: floor height in yards (visual only; rooms are flat). Default 0. */
  elevation?: Yards;
}

export interface CorridorDef {
  id: string;
  from: RoomId;
  to: RoomId;
  /** Walkable strip between rooms. */
  shape: Polygon;
  /**
   * +spatial: sloped spans along the corridor (stairs/ramps), ordered from `from` to `to`.
   * Outside every span the corridor is flat at the nearest span end's elevation. Visual height
   * only: the sim stays 2D and paths through corridors exactly as before.
   */
  rises?: CorridorRise[];
}

/** +spatial: a sloped span of a corridor (yards, world space). Height lerps linearly start → end. */
export interface CorridorRise {
  kind: 'ramp' | 'stairs';
  /** Centreline point where the slope begins (the `from` side). */
  start: Vec2;
  /** Centreline point where the slope ends (the `to` side). */
  end: Vec2;
  startElevation: Yards;
  endElevation: Yards;
  /** Stairs: riser count (renderer hint). */
  steps?: number;
}

/** +spatial: door ids (unique per dungeon). */
export type DoorId = string;
export type DoorStyle = 'wood' | 'stone' | 'portcullis' | 'vines';

/**
 * +spatial: a door across a corridor. A closed door is a dynamic blocker (movement + pathing).
 * Its blocking footprint is a `width` × `depth` rectangle centred on `position`, with `depth`
 * along `facing`. A seal (`sealsEncounter`) closes once that encounter is in progress and every
 * living party member is inside the arena (or after SEAL_GRACE_MS, pulling stragglers in), and
 * opens again when the encounter ends (kill or wipe).
 */
export interface DoorDef {
  id: DoorId;
  /** The two rooms the door separates; `facing` points from between[0] toward between[1]. */
  between: [RoomId, RoomId];
  /** Corridor the door stands in (renderer hint). */
  corridorId?: string;
  position: Vec2;
  /** Across the corridor (yards). Should exceed the corridor width so nothing slips past the ends. */
  width: Yards;
  /** Door-plane normal (radians), from between[0] toward between[1]. */
  facing: Radians;
  /** Blocking depth along `facing` (default DOOR_BLOCK_DEPTH). */
  depth?: Yards;
  style: DoorStyle;
  initially: 'open' | 'closed';
  sealsEncounter?: EncounterId;
}

/** +spatial: default blocking depth of a closed door (yards). */
export const DOOR_BLOCK_DEPTH: Yards = 2;
/** +spatial: a seal waits this long after encounter_start for the party to get inside, then pulls stragglers in. */
export const SEAL_GRACE_MS: Ms = 4000;

/** +spatial: authored decorative placement hint (renderer only; never blocks anything). */
export interface PropHint {
  /** Renderer-defined prop kind (e.g. 'arch', 'idol', 'coiled_serpent'). */
  kind: string;
  pos: Vec2;
  facing?: Radians;
  scale?: number;
  roomId?: RoomId;
  corridorId?: string;
}

/** +spatial: runtime door state (serializable, lives in World.doors). */
export interface DoorState {
  closed: boolean;
  /** Sim time of the last open/close (0 = initial state). */
  changedAt: Ms;
}

export interface SpawnPoint {
  npcId: NpcId;
  pos: Vec2;
  facing: Radians;
  /** +patrols: out-of-combat movement (default: stand still; members of `path` packs follow the leader). */
  movement?: SpawnMovement;
}

/**
 * +patrols: per-spawn out-of-combat movement (TrinityCore creature.MovementType 0 idle / 1 random).
 * Waypoint patrols are pack-level (`PackDef.path`): the leader walks, the rest follow in formation.
 */
export type SpawnMovement =
  /** Stands at its spawn and turns a little now and then. */
  | { type: 'idle' }
  /** Walks to random points within `radius` of its spawn, pausing `pauseMs` (default WANDER_PAUSE_MS) at each. */
  | { type: 'wander'; radius: Yards; pauseMs?: readonly [Ms, Ms] }
  /**
   * +zone: field spawns only. Walks `points` in order ('loop' back to the first, or 'pingpong'),
   * pausing `pauseMs` at each; pulled mobs leash from where they were pulled and resume the route.
   */
  | { type: 'patrol'; points: readonly Vec2[]; mode: 'loop' | 'pingpong'; pauseMs?: readonly [Ms, Ms] };

/** +patrols: one waypoint of a patrol path. */
export interface PatrolPoint {
  pos: Vec2;
  /** Pause here on arrival (ms, default 0). */
  waitMs?: Ms;
  /** Face this way while paused (radians). */
  facing?: Radians;
}

/** +patrols: a waypoint patrol walked by the pack leader (spawns[0]); members follow in formation. */
export interface PatrolPath {
  points: PatrolPoint[];
  /** 'loop': after the last point head back to the first; 'pingpong': walk the path backwards. */
  mode: 'loop' | 'pingpong';
  /** Run instead of walking (default: walk). */
  run?: boolean;
}

/**
 * +patrols: how a pack's members pull (docs/design/mob-movement.md).
 *   - 'linked': pulling any member pulls every member (a TrinityCore formation / linked group).
 *   - 'loose': every member aggros on its own (own aggro radius + line of sight); others only join
 *     through call-for-help (ASSIST_RADIUS of the mob entering combat, in its line of sight, after
 *     ASSIST_DELAY_MS) — so edge pulls, line-of-sight pulls and catching a patrol away can split it.
 */
export type PackLink = 'linked' | 'loose';

export interface PackDef {
  id: PackId;
  roomId: RoomId;
  spawns: SpawnPoint[];
  /** Party units within this distance of any member aggro the pack. */
  aggroRadius: Yards;
  /** Social aggro: when one member is pulled, every member within this radius of it joins. */
  socialRadius?: Yards;
  /** Patrol waypoints (loop). Legacy: every member walks the loop at run speed keeping its spawn offset. */
  patrol?: Vec2[];
  /**
   * +patrols: opt into the WoW social model (per-mob aggro with line of sight, links and
   * call-for-help, leash). Absent = legacy: `socialRadius` chaining, no line of sight.
   */
  link?: PackLink;
  /** +patrols: call-for-help radius for this pack's mobs (default ASSIST_RADIUS). Only with `link`. */
  assistRadius?: Yards;
  /** +patrols: waypoint patrol for the leader (spawns[0]); the others follow in formation. Needs `link`. */
  path?: PatrolPath;
  /** If set, this pack is a boss encounter. */
  encounterId?: EncounterId;
}

export interface DungeonDef {
  id: DungeonId;
  name: string;
  timerMs: Ms;
  forcesRequired: number;
  entrance: Vec2;
  entranceFacing: Radians;
  rooms: RoomDef[];
  corridors: CorridorDef[];
  /** Blocking obstacles inside rooms (pillars etc.). */
  obstacles?: Polygon[];
  /** +spatial: doors and boss seals (dynamic blockers). */
  doors?: DoorDef[];
  /** +spatial: authored hero-prop hints for renderers (decorative only). */
  props?: PropHint[];
  /** +world-resources: breakables, hidden caches and reagent nodes (never block movement). */
  worldObjects?: WorldObjectDef[];
  packs: PackDef[];
  /** Ordered pack ids (MDT-style route) including boss packs. Bots follow it. */
  route: PackId[];
  /** The run completes when all of these are dead and forces >= required. */
  requiredEncounters: EncounterId[];
  /** Overall bounds (for renderers and the nav grid). */
  bounds: Rect;
  /** +field: an open field / town (no timer, respawning spawn points, portals). See FieldDef. */
  field?: FieldDef;
  /** +glide: heights for free flight (terrain floor, obstacle tops, platforms, updrafts). Absent = flat floor 0, every obstacle solid. */
  airspace?: AirspaceDef;
  /**
   * +field: normal-difficulty mob level of a levelled dungeon (the starter dungeon). Its NPCs spawn
   * at this level (levelMult scaling) on top of any keystone scaling.
   */
  mobLevel?: number;
}

// ============================================================================
// 13. Keystone / M+
// ============================================================================

export interface KeystoneConfig {
  /** 2..15 (PLAN §6). */
  level: number;
  affixes: AffixId[];
}

export type RunPhase = 'pre' | 'running' | 'completed' | 'depleted';

export interface MPlusState {
  phase: RunPhase;
  level: number;
  affixes: AffixId[];
  timerMs: Ms;
  /** Sim time when the run started (null before). */
  startedAt: Ms | null;
  /** Elapsed run time incl. death penalties. */
  elapsedMs: Ms;
  forces: number;
  forcesRequired: number;
  deaths: number;
  deathPenaltyMs: Ms; // total added so far
  bossesKilled: EncounterId[];
  packsKilled: PackId[];
  completedAt: Ms | null;
  /** 0 = depleted, 1..3 = upgrade levels. Null until completed. */
  chest: 0 | 1 | 2 | 3 | null;
  inCombat: boolean;
  /** Sim time the current combat segment began (null when out of combat). */
  combatSince: Ms | null;
  outOfCombatMs: Ms;
  pulls: number;
  wipes: number;
  /** +integrator (mplus): open pull segment / encounter / wipe-respawn bookkeeping (serializable). */
  book?: MPlusBook;
  /** +personas: every party member left the group (phase is then 'depleted', chest 0). */
  abandoned?: boolean;
}

/** +integrator (mplus): bookkeeping kept on world.mplus so it survives snapshot/restore. */
export interface MPlusBook {
  segment: { pull: number; startMs: Ms; packIds: PackId[]; killedIds: UnitId[] } | null;
  encounter: { encounterId: EncounterId; packId: PackId | null; startedAt: Ms } | null;
  respawnAt: Ms | null;
  /** Dead party members (not a wipe) auto-release and respawn at the checkpoint at this time. */
  releaseAt?: Ms | null;
}

export interface ScalingMults {
  hp: number;
  damage: number;
}

export interface RunResult {
  dungeonId: DungeonId;
  level: number;
  affixes: AffixId[];
  completed: boolean;
  timed: boolean;
  chest: 0 | 1 | 2 | 3;
  elapsedMs: Ms;
  timerMs: Ms;
  deaths: number;
  deathPenaltyMs: Ms;
  forces: number;
  forcesRequired: number;
  outOfCombatMs: Ms;
  pulls: number;
  wipes: number;
  /** +world-resources: bosses killed this run (mplus.bossesKilled.length). */
  bossesKilled?: number;
  /** +world-resources: world pickups the looter collected (absent when the dungeon has none). */
  worldFinds?: WorldFinds;
  /** +world-resources: breakables broken / caches opened (end-of-run "Found in the dungeon" line). */
  worldStats?: WorldLootStats;
  /** +personas: everyone left mid-run (not completed). Absent otherwise. */
  abandoned?: boolean;
  /** +personas: party members who left the group mid-run. Absent when none did. */
  departed?: number;
}

// ============================================================================
// 14. Units and World state
// ============================================================================

/** Current cast or channel. */
export interface CastState {
  abilityId: AbilityId;
  kind: 'cast' | 'channel';
  targetId: UnitId | null;
  point: Vec2 | null;
  startedAt: Ms;
  /** Absolute end; pushback moves it. */
  endsAt: Ms;
  /** Total (hasted) duration at start. */
  durationMs: Ms;
  interruptible: boolean;
  pushbacks: number;
  /** Channels only. */
  tickIntervalMs?: Ms;
  nextTickAt?: Ms;
  ticksDone?: number;
  /** +sim: rotation rework (F3). world.nextSeq when the cast started: orders same-tick aura applications for the castResolved guard. */
  startSeq?: number;
}

/** A command waiting in the spell queue (PLAN §8). */
export interface QueuedCast {
  abilityId: AbilityId;
  targetId: UnitId | null;
  point: Vec2 | null;
  queuedAt: Ms;
}

export interface CooldownState {
  /** Available charges now. */
  charges: number;
  maxCharges: number;
  /** When the next charge comes back (null when full). */
  rechargeAt: Ms | null;
  /** Duration used for the current recharge (for UI swipe). */
  rechargeDurationMs: Ms;
}

export interface SwingState {
  speedMs: Ms;
  /** Next swing time (can be in the past while out of range; swings when able). */
  nextAt: Ms;
  /** +autoattack: 'melee' (default), 'ranged' (Auto Shot at `range`) or 'staff' (a caster's bonk). */
  kind?: 'melee' | 'ranged' | 'staff';
  /** +autoattack: Auto Shot range (yards). */
  range?: Yards;
  /** +autoattack: dual wield: the off hand's own timer (mult = share of the main-hand damage). */
  oh?: { speedMs: Ms; nextAt: Ms; mult: number };
  /** +autoattack: haste the running interval was timed with (a change rescales what is left). */
  haste?: number;
  /** +autoattack: a due swing waits: target out of range / not in front (UI error, throttled). */
  blocked?: 'range' | 'facing' | null;
}

export interface DrState {
  /** Number of CCs applied in the current DR window (0 = next is full). */
  count: number;
  /** When the DR resets (last CC end + DR_RESET_MS). */
  resetAt: Ms;
}

/** Movement intent. Spatial integrates it each tick. */
export type MoveIntent =
  | { mode: 'dir'; dir: Vec2 } // normalized direction (player WASD)
  | { mode: 'dest'; dest: Vec2; path: Vec2[]; pathIndex: number; walk?: boolean } // A* path to a destination (+patrols: walk = NPC walk speed)
  | { mode: 'forced'; to: Vec2; speed: number; until: Ms }; // knockback/grip/dash, cannot act

export interface ThreatEntry {
  threat: number;
  /** Last time this unit added threat (debug/UI). */
  lastAt: Ms;
}

/** NPC threat table (lives on the NPC unit). */
export interface ThreatTable {
  entries: Record<UnitId, ThreatEntry>;
  victimId: UnitId | null;
  nextReselectAt: Ms;
  /** Taunt: forced victim until `until`. */
  taunt: { by: UnitId; until: Ms } | null;
  /** Fixate beats taunt. */
  fixate: { on: UnitId; until: Ms } | null;
  /** Taunt DR count (flagged mobs). */
  tauntDrCount: number;
  tauntDrResetAt: Ms;
}

export type AiMode = 'idle' | 'patrol' | 'combat' | 'evading' | 'dead';

/** Per-NPC AI state. */
export interface AiState {
  mode: AiMode;
  /** Next scheduled cast per enemy ability id (absolute ms). */
  timers: Record<AbilityId, Ms>;
  /** Current encounter phase index (bosses). */
  phaseIndex: number;
  engagedAt: Ms | null;
  nextEvadeCheckAt: Ms;
  patrolIndex: number;
  /** +patrols: out-of-combat movement state (opt-in packs; created lazily). */
  roam?: RoamState;
  /** +patrols: pending call for help (helpers picked at the call, they attack at `at`). */
  assist?: { at: Ms; helperIds: UnitId[]; attackerId: UnitId } | null;
  /** +patrols: already called for help this combat (or was itself called in: helpers never chain). */
  calledHelp?: boolean;
}

/** +patrols: serializable out-of-combat movement state of one NPC. */
export interface RoamState {
  /** Next idle decision (fidget turn, next wander point, leave a patrol point). */
  nextAt: Ms;
  /** Patrol direction along the path (+1 forward, -1 backward for ping-pong). */
  dir: 1 | -1;
  /** Leader only: breadcrumbs of where it walked (newest first), for formation members. */
  trail?: Vec2[];
  /** Where the unit entered combat; it returns here on evade and resumes (null = its spawn). */
  resumeAt?: Vec2 | null;
  /** Wanderer: walking to a point right now. */
  going?: boolean;
  /** Wanderer: moves left in the current burst before the next pause. */
  steps?: number;
}

export interface Unit {
  id: UnitId;
  name: string;
  kind: UnitKind;
  faction: Faction;
  controller: Controller;
  role: Role | null;
  classId: ClassId | null;
  /** +specs: the specialisation the unit's ClassDef carries (players; absent = base kit). */
  specId?: SpecId;
  npcId: NpcId | null;
  tier: NpcTier | null;
  pos: Vec2;
  facing: Radians;
  /** Collision radius. */
  radius: Yards;
  combatReach: Yards;
  /** Base move speed (yards/sec) before aura mods. */
  moveSpeed: Yards;
  move: MoveIntent | null;
  /** True if the unit changed position this tick (casting checks, range leeway). */
  moving: boolean;
  hp: number;
  maxHp: number;
  alive: boolean;
  diedAt: Ms | null;
  /** Present only for resources the unit has. */
  resources: Partial<Record<ResourceId, ResourcePool>>;
  stats: Stats;
  /** Keystone scaling for NPCs (1 for players). Damage dealt multiplied by scaling.damage. */
  scaling: ScalingMults;
  auras: AuraInstance[];
  cooldowns: Record<AbilityId, CooldownState>;
  gcdUntil: Ms;
  cast: CastState | null;
  queued: QueuedCast | null;
  swing: SwingState | null;
  /** Melee damage per swing (NPCs: from NpcDef; players: ClassDef.melee). */
  meleeDamage: Amount | null;
  /** Selected target (players) / current victim (NPCs). */
  targetId: UnitId | null;
  /** School lockouts from interrupts: school → locked until. */
  lockouts: Partial<Record<School, Ms>>;
  dr: Partial<Record<CcType, DrState>>;
  /** NPCs only. */
  threat: ThreatTable | null;
  ai: AiState | null;
  packId: PackId | null;
  encounterId: EncounterId | null;
  home: Vec2;
  homeFacing: Radians;
  inCombat: boolean;
  /** Recent damage taken for "last N seconds" formulas. Pruned to 10 s. */
  recentDamage: { at: Ms; amount: number }[];
  /** Flags derived from NpcDef. */
  ccImmune: boolean;
  tauntDr: boolean;
  /** Summoned by. */
  summonerId: UnitId | null;
  /** Renderer hint. */
  color: string;
  /**
   * +lead: rotation rework (F2). Derived, serialisable: base ability → replacement. Rebuilt by combat only
   * when an aura with `overrides` is applied or removed; absent when empty.
   */
  abilityOverrides?: Record<AbilityId, AbilityId>;
  /** +personas: AFK / wandering off (absent = here). Set only by Command 'presence'. */
  presence?: Presence;
  /** +personas: left the group (Command 'leaveParty'): out of play for good. Absent = in the group. */
  departed?: boolean;
  /** +field: unit level (heroes: hero level; NPCs: mob level). Absent = unlevelled (M+ keys). */
  level?: number;
  /** +field: an elite field mob (nameplate dragon, XP bonus). */
  elite?: boolean;
  /** +field: the field spawn point this NPC belongs to. */
  spawnId?: string;
  /**
   * +jump: airborne from `at` until `until`; `pending` = the last move / stop sent mid-air.
   * +glide: `air` = free flight (glider open, falling, or a hop off a platform): absolute height and
   * velocity; `until` no longer applies (the unit lands when `air.z` reaches the floor).
   */
  jump?: { at: Ms; until: Ms; pending: { dir?: Vec2; dest?: Vec2; stop?: boolean } | null; air?: AirState };
  /** +glide: standing on a floating platform (AirspaceDef.platforms) at height `z` (absent = on the terrain). */
  perch?: { z: Yards; platformId: string };
  /**
   * +autoattack: players' auto-attack toggle (WoW's attack state). On: swings / Auto Shot at the
   * target. Started by right-click, Attack, or a weapon ability; stopped by the target dying or
   * becoming friendly / invalid, clearing the target, death, stealth or toggling off. NPCs ignore
   * it (they swing whenever in combat).
   */
  autoAttack?: boolean;
  /** +acolyte-specs (S7, combat/pets.ts): a friendly summoned pet (owner, lifetime, attack timer). */
  pet?: PetState;
  /** +ranger-specs (S7 companions, combat/pets.ts, persistent pets): a persistent companion's owner and sync state. */
  companion?: CompanionState;
}

export interface Telegraph {
  id: TelegraphId;
  sourceId: UnitId;
  abilityId: AbilityId;
  /** Current placement (followed units update it each tick). */
  area: WorldShape;
  affects: Affects;
  createdAt: Ms;
  resolvesAt: Ms;
  /** Unit to follow when `follow` was set. */
  followUnitId: UnitId | null;
  effects: EffectSpec[];
  repeatsLeft: number;
  repeatIntervalMs: Ms;
  /** +encounters: avoid (default) / deadly / soak (stand in it). */
  intent?: HazardIntent;
  /** +encounters: soak telegraphs: units needed and eligible units inside now. */
  soak?: { min: number; inside: number };
  /** +encounters: kind of mechanic that owns it (e.g. 'soak', 'charge', 'wave'). */
  mechanic?: MechanicSpec['kind'];
}

export interface Zone {
  id: ZoneId;
  defId: string;
  sourceId: UnitId;
  abilityId: AbilityId;
  area: WorldShape;
  affects: Affects;
  createdAt: Ms;
  expiresAt: Ms;
  tick: { intervalMs: Ms; effects: EffectSpec[] } | null;
  nextTickAt: Ms | null;
  insideAuraId: AuraId | null;
  /** Units currently holding insideAura from this zone. */
  inside: UnitId[];
  /** +encounters: hint for bots/renderers. */
  intent?: HazardIntent;
  /** +encounters: moving/growing hazards (ai/mechanics updates `area` each tick). */
  motion?: ZoneMotionState;
  /** +lead: rotation rework (F9). Caster stats captured at creation (ZoneEffect.snapshot). */
  snapshot?: { power: number; crit: number; damageDoneMult: number; healingDoneMult: number };
  /** +basekits: ZoneEffect.triggerOnce (a trap). */
  triggerOnce?: boolean;
  /** +basekits: ZoneEffect.onExpire (runs on natural expiry only; a dead caster's zones skip it). */
  onExpire?: EffectSpec[];
}

export interface PackState {
  id: PackId;
  memberIds: UnitId[];
  state: 'idle' | 'engaged' | 'dead';
  engagedAt: Ms | null;
}

export interface EncounterState {
  encounterId: EncounterId;
  packId: PackId;
  bossIds: UnitId[];
  startedAt: Ms;
  phaseIndex: number;
  /** null while in progress. */
  result: 'kill' | 'wipe' | null;
}

/** Complete serializable simulation state. snapshot() = structured clone of this. */
export interface World {
  /** Number of ticks stepped so far. */
  tick: number;
  /** Sim time (tick * TICK_MS). */
  time: Ms;
  seed: number;
  rngState: RngState;
  dungeonId: DungeonId;
  /** Unit storage. Iterate via `unitOrder` for determinism. */
  units: Record<UnitId, Unit>;
  /** Insertion order of unit ids (never reordered; dead units remain). */
  unitOrder: UnitId[];
  /** Party unit ids in slot order (tank, healer, dps, dps). */
  partyIds: UnitId[];
  packs: Record<PackId, PackState>;
  telegraphs: Telegraph[];
  zones: Zone[];
  encounter: EncounterState | null;
  /** Encounters already killed (never restart). */
  encountersDone: EncounterId[];
  mplus: MPlusState;
  /** Monotonic id counter for units/auras/telegraphs/zones. */
  nextSeq: number;
  /** Events emitted during the current tick (flushed by step()). */
  events: SimEvent[];
  /** +spatial: door open/closed state by door id (set by spatial.init for dungeons with doors). */
  doors?: Record<DoorId, DoorState>;
  /** +encounters: live mechanic state (soaks, tethers, charges, intermissions...). */
  mechanics?: MechanicsState;
  /** +world-resources: breakables / caches / nodes (static order = DungeonDef.worldObjects). */
  worldObjects?: WorldObjectState[];
  /** +world-resources: loot on the ground (collected pickups are removed). */
  pickups?: PickupState[];
  /** +world-resources: who may break/collect, and the running tally. */
  worldLoot?: WorldLootState;
  /** +field: open-field state (spawn points, respawn timers). Present only on field maps. */
  field?: FieldState;
  /** +rogue-specs (combat/clones.ts): echoes waiting to land (Silhouettes). Absent when none. */
  echoes?: PendingEcho[];
}

// ============================================================================
// 15. Commands (player AND bot input)
// ============================================================================

export type Command =
  /** Move along a direction (unit vector; zero vector = stop) or path to a destination. */
  | { type: 'move'; actorId: UnitId; dir?: Vec2; dest?: Vec2 }
  | { type: 'stop'; actorId: UnitId }
  /** Face an angle or a unit (instant turn). */
  | { type: 'face'; actorId: UnitId; facing?: Radians; targetId?: UnitId }
  /** Select a target (null clears). */
  | { type: 'target'; actorId: UnitId; targetId: UnitId | null }
  | { type: 'cast'; actorId: UnitId; abilityId: AbilityId; targetId?: UnitId; point?: Vec2 }
  | { type: 'cancelCast'; actorId: UnitId }
  /** Start the run / start an encounter interaction / pull lever etc. targetId optional. */
  | { type: 'interact'; actorId: UnitId; targetId?: UnitId; what?: 'startRun' | 'releaseSpirit' | (string & {}) }
  /**
   * +personas: a party chat line (or an emote with `emote`). No gameplay effect: the sim records it
   * as a 'chat' event so every client (and the replay) shows the same log. `lineId`: the bots'
   * line-pool id (lines.ts), for tools; the text is what is shown. Text is capped at CHAT_MAX_LEN.
   */
  | { type: 'say'; actorId: UnitId; text: string; lineId?: string; emote?: boolean }
  /** +personas: AFK / wandering-off flag for party frames (null = back). No gameplay effect. */
  | { type: 'presence'; actorId: UnitId; status: Presence | null }
  /**
   * +personas: leave the group mid-run (rage quit, AFK kick, abandon). The unit is out of play for
   * good: no actions, never revived, not counted as a death. Every member gone = run abandoned.
   * Bots only (the room does not accept it from clients: a human leaving is a bot takeover).
   */
  | { type: 'leaveParty'; actorId: UnitId; reason?: LeaveReason; blameId?: UnitId; cause?: string }
  /**
   * +field: the hero reached `level` (progression decided it). Rescales max HP / power with levelMult,
   * refills HP and resources, and emits 'level_up'. Player units only; ignored when not higher.
   */
  | { type: 'setLevel'; actorId: UnitId; level: number }
  /**
   * +jump: a WoW-style hop (JUMP_MS airborne, ballistic JUMP_HEIGHT arc for renderers). Keeps the
   * current run (momentum); move / stop commands sent mid-air apply on landing. No cast-time spells
   * while airborne (a hard cast in progress is cancelled); hostile ground zones don't tick on you.
   */
  | { type: 'jump'; actorId: UnitId }
  /**
   * +glide: open (`on`) / fold the glider. Opens only while airborne (a jump, a fall); folding while
   * inside an updraft is ignored. Players only (bots never glide). See AirState.
   */
  | { type: 'glide'; actorId: UnitId; on: boolean }
  /**
   * +autoattack: toggle auto-attack (WoW "Attack" / right-click on a hostile). `targetId` selects
   * that unit first. Casters (staff) start only in melee range; otherwise it is just a target.
   */
  | { type: 'autoAttack'; actorId: UnitId; on: boolean; targetId?: UnitId }
  /** +prof: the hero's profession skills (progression decided them); gates which nodes it may gather. */
  | { type: 'setGatherSkill'; actorId: UnitId; foraging: number; mining: number };

/** +personas: a party member's away state (Unit.presence). */
export type Presence = 'afk' | 'wandering';
/** +personas: why a member left the group (PartyLeaveEvent.reason). */
export type LeaveReason = 'ragequit' | 'afk' | 'abandon' | (string & {});
/** +personas: longest chat line the sim records (longer text is cut). */
export const CHAT_MAX_LEN = 160;

export type CommandType = Command['type'];

/** Why a cast attempt failed (casting system, for UI error text and bot logic). */
export type CastFailReason =
  | 'unknown_ability'
  | 'dead'
  | 'cannot_act'
  | 'silenced'
  | 'school_locked'
  | 'on_gcd'
  | 'casting'
  | 'on_cooldown'
  | 'no_resource'
  | 'no_target'
  | 'invalid_target'
  | 'out_of_range'
  | 'not_facing'
  | 'moving'
  | 'rooted'
  | 'condition_failed'
  /** +field: the hero's level is below ClassDef.unlocks for this ability. */
  | 'locked';

export type CastResult = { ok: true; queued: boolean } | { ok: false; reason: CastFailReason };

// ============================================================================
// 16. Events (Warcraft Logs-shaped)
// ============================================================================

/** Sim hit types. Export maps them to WCL numeric hitType (see log/). */
export type HitType = 'hit' | 'crit' | 'miss' | 'dodge' | 'parry' | 'block' | 'immune' | 'absorb';

/** Fields shared by every event. Ids are sim ids (strings); WCL export maps to numbers. */
export interface EventBase {
  timestamp: Ms;
  sourceID?: UnitId;
  targetID?: UnitId;
  abilityGameID?: AbilityId;
}

export interface DamageEvent extends EventBase {
  type: 'damage';
  sourceID: UnitId;
  targetID: UnitId;
  abilityGameID: AbilityId;
  school: School;
  /** HP actually removed (after absorbs). */
  amount: number;
  /** Damage beyond remaining HP. */
  overkill: number;
  /** Part soaked by absorbs. */
  absorbed: number;
  /** Removed by block. */
  blocked?: number;
  /** Pre-mitigation amount. */
  unmitigatedAmount: number;
  hitType: HitType;
  isAoE: boolean;
  /** True for DoT/zone ticks. */
  tick?: boolean;
  /** +lead: rotation rework (F7). Stacks read by Amount.perStack for this hit (heavy-hit VFX, metrics). */
  stacks?: number;
  /** +autoattack: a white hit: main hand, off hand or Auto Shot. */
  swing?: 'mh' | 'oh' | 'shot';
}

export interface HealEvent extends EventBase {
  type: 'heal';
  sourceID: UnitId;
  targetID: UnitId;
  abilityGameID: AbilityId;
  amount: number;
  overheal: number;
  hitType: 'hit' | 'crit';
  tick?: boolean;
  /** +encounters: healing eaten by heal-absorb auras (not part of amount/overheal). */
  absorbed?: number;
  /** +lead: rotation rework (F7). Stacks read by Amount.perStack for this heal. */
  stacks?: number;
}

/** An absorb shield soaked damage. sourceID = shield caster, targetID = shielded unit. */
export interface AbsorbedEvent extends EventBase {
  type: 'absorbed';
  sourceID: UnitId;
  targetID: UnitId;
  /** The absorb aura's ability. */
  abilityGameID: AbilityId;
  attackerID: UnitId;
  /** The damaging ability. */
  extraAbilityGameID: AbilityId;
  amount: number;
}

export interface CastEventBase extends EventBase {
  sourceID: UnitId;
  abilityGameID: AbilityId;
}
export interface BeginCastEvent extends CastEventBase {
  type: 'begincast';
  /** Cast/channel duration (ms). */
  duration: Ms;
  interruptible: boolean;
}
export interface CastEvent extends CastEventBase {
  type: 'cast';
  /** +lead: rotation rework (F12). Cast time actually paid (0 = instant, includes pushback); channels: planned duration. */
  castMs?: Ms;
  /** +lead: rotation rework (F12). Set on a channel's cast event. */
  channel?: true;
}

export interface AuraEvent extends EventBase {
  type:
    | 'applybuff'
    | 'applydebuff'
    | 'removebuff'
    | 'removedebuff'
    | 'refreshbuff'
    | 'refreshdebuff'
    | 'applybuffstack'
    | 'applydebuffstack'
    | 'removebuffstack'
    | 'removedebuffstack';
  sourceID: UnitId;
  targetID: UnitId;
  /** Aura id (WCL uses the spell id; ours equals the AuraId). */
  abilityGameID: AuraId;
  stack?: number;
  /** Absorb amount for shield auras. */
  absorb?: number;
  /** +lead: rotation rework (F12). Why it was removed (remove* events only). */
  reason?: AuraRemoveReason;
}

/** +lead: rotation rework (F12). Aura removal reasons (same set as CombatSystem.removeAura). */
export type AuraRemoveReason = 'expired' | 'consumed' | 'dispelled' | 'cancelled' | 'death' | 'replaced' | 'evade';

/** +lead: rotation rework (F12). Sim-only. Something was granted and lost to a cap (WCL export skips it). */
export interface OvercapEvent extends EventBase {
  type: 'overcap';
  sourceID: UnitId;
  /** The unit that held the capped aura / cooldown. */
  targetID?: UnitId;
  kind: 'auraStacks' | 'charge';
  /** Aura id (auraStacks) or ability id (charge). */
  abilityGameID: AbilityId | AuraId;
  /** Stacks or charges lost. */
  amount: number;
}

export interface InterruptEvent extends EventBase {
  type: 'interrupt';
  sourceID: UnitId;
  targetID: UnitId;
  abilityGameID: AbilityId;
  /** The interrupted ability. */
  extraAbilityGameID: AbilityId;
}

export interface DispelEvent extends EventBase {
  type: 'dispel';
  sourceID: UnitId;
  targetID: UnitId;
  abilityGameID: AbilityId;
  /** Removed aura. */
  extraAbilityGameID: AuraId;
  isBuff: boolean;
}

export interface DeathEvent extends EventBase {
  type: 'death';
  targetID: UnitId;
  /** Killing blow source/ability if known. */
  sourceID?: UnitId;
  abilityGameID?: AbilityId;
}

export interface ResourceChangeEvent extends EventBase {
  type: 'resourcechange';
  sourceID: UnitId;
  targetID: UnitId;
  abilityGameID: AbilityId;
  resource: ResourceId;
  resourceChange: number;
  waste: number;
  /** Value after change. */
  resourceAmount: number;
  maxResourceAmount: number;
}

export interface SummonEvent extends EventBase {
  type: 'summon';
  sourceID: UnitId;
  targetID: UnitId;
  abilityGameID: AbilityId;
}

/** +integrator: sim-only. A cast/channel stopped without an interrupt (moved, cc, death, clipped...). */
export interface CastCancelledEvent extends EventBase {
  type: 'cast_cancelled';
  sourceID: UnitId;
  abilityGameID: AbilityId;
  reason: 'player' | 'moved' | 'cc' | 'death' | 'target_lost' | 'out_of_range';
}

export interface TelegraphStartEvent extends EventBase {
  type: 'telegraph_start';
  telegraphId: TelegraphId;
  sourceID: UnitId;
  abilityGameID: AbilityId;
  area: WorldShape;
  resolvesAt: Ms;
}
export interface TelegraphEndEvent extends EventBase {
  type: 'telegraph_end';
  telegraphId: TelegraphId;
  sourceID: UnitId;
  abilityGameID: AbilityId;
  /** Units hit at resolution. */
  hitIds: UnitId[];
}

export interface EncounterStartEvent extends EventBase {
  type: 'encounter_start';
  encounterId: EncounterId;
  name: string;
}
export interface EncounterEndEvent extends EventBase {
  type: 'encounter_end';
  encounterId: EncounterId;
  name: string;
  kill: boolean;
}
/** +spatial: sim-only. A door opened (seal released on encounter end, or wipe). */
export interface DoorOpenEvent extends EventBase {
  type: 'door_open';
  doorId: DoorId;
  encounterId?: EncounterId;
}
/** +spatial: sim-only. A door closed (boss seal). `pulledIds`: stragglers moved inside the arena. */
export interface DoorCloseEvent extends EventBase {
  type: 'door_close';
  doorId: DoorId;
  encounterId?: EncounterId;
  pulledIds?: UnitId[];
}
export interface PullStartEvent extends EventBase {
  type: 'pull_start';
  /** 1-based pull counter. */
  pull: number;
  packIds: PackId[];
}
export interface PullEndEvent extends EventBase {
  type: 'pull_end';
  pull: number;
  packIds: PackId[];
  /** Enemies killed in this pull (npc unit ids). */
  killedIds: UnitId[];
  wipe: boolean;
}
export interface RunStartEvent extends EventBase {
  type: 'run_start';
  dungeonId: DungeonId;
  level: number;
  affixes: AffixId[];
  timerMs: Ms;
}
export interface RunEndEvent extends EventBase {
  type: 'run_end';
  result: RunResult;
}
export interface ForcesUpdateEvent extends EventBase {
  type: 'forces_update';
  forces: number;
  forcesRequired: number;
  /** NPC that gave forces. */
  targetID?: UnitId;
}

/** +personas: sim-only. A party chat line or emote (Command 'say'). */
export interface ChatEvent extends EventBase {
  type: 'chat';
  sourceID: UnitId;
  text: string;
  lineId?: string;
  emote?: boolean;
}
/** +personas: sim-only. A member went AFK / wandered off (status) or came back (null). */
export interface PresenceEvent extends EventBase {
  type: 'presence';
  sourceID: UnitId;
  status: Presence | null;
}
/** +personas: sim-only. A member left the group (Command 'leaveParty'). */
export interface PartyLeaveEvent extends EventBase {
  type: 'party_leave';
  sourceID: UnitId;
  reason: LeaveReason;
  /** Who they blame (rage quits). */
  blameId?: UnitId;
  /** Short structured cause for narration ("died to Frontal Slam (3rd time)"). */
  cause?: string;
}

/** +jump: sim-only. A unit jumped ('jump') or landed ('land'). */
export interface JumpEvent extends EventBase {
  type: 'jump' | 'land';
  sourceID: UnitId;
  /** +glide: a landing from free flight (glide / fall): the yards dropped since the highest point (soft, no damage). */
  drop?: number;
  /** +glide: landed on this platform. */
  platformId?: string;
}
/** +glide: sim-only. The glider opened (`on`) or folded. */
export interface GlideEvent extends EventBase {
  type: 'glide';
  sourceID: UnitId;
  on: boolean;
}

/** +field: sim-only. A hero levelled up (Command 'setLevel'); renderers play the ding for everyone. */
export interface LevelUpEvent extends EventBase {
  type: 'level_up';
  sourceID: UnitId;
  level: number;
  /** Levels gained at once (a multi-level jump is one bigger celebration). */
  gained: number;
}
/** +field: sim-only. A player unit stepped into a portal; the client loads `to`. */
export interface PortalEvent extends EventBase {
  type: 'portal';
  sourceID: UnitId;
  portalId: string;
  to: DungeonId;
  toPortal?: string;
}
/** +zone: sim-only. A hero travelled to a waystone (FieldWaystoneDef); renderers snap the camera. */
export interface WaystoneEvent extends EventBase {
  type: 'waystone';
  sourceID: UnitId;
  waystoneId: string;
  from: Vec2;
  to: Vec2;
}
/**
 * +prof: gathering at a resource node (field/gather.ts): a channel started, was cancelled (moving,
 * damage, combat, a cast, death), finished (`done`: the node is depleted until it respawns), or was
 * refused at the start (`reason`: skill too low, too far, depleted, in combat).
 */
export interface GatherEvent extends EventBase {
  type: 'gather';
  sourceID: UnitId;
  nodeId: string;
  kind: string;
  profession: GatherProfession;
  tier: number;
  state: 'start' | 'cancel' | 'done' | 'refused';
  durationMs?: Ms;
  reason?: 'moved' | 'damage' | 'combat' | 'cast' | 'dead' | 'skill' | 'far' | 'depleted' | 'busy';
}
/** +field: sim-only. A field spawn point (re)spawned a mob. */
export interface FieldSpawnEvent extends EventBase {
  type: 'field_spawn';
  sourceID: UnitId;
  spawnId: string;
  level: number;
}

export type SimEvent =
  | DamageEvent
  | HealEvent
  | AbsorbedEvent
  | BeginCastEvent
  | CastEvent
  | AuraEvent
  | InterruptEvent
  | DispelEvent
  | DeathEvent
  | ResourceChangeEvent
  | SummonEvent
  | CastCancelledEvent // +integrator
  | TelegraphStartEvent
  | TelegraphEndEvent
  | EncounterStartEvent
  | EncounterEndEvent
  | PullStartEvent
  | PullEndEvent
  | RunStartEvent
  | RunEndEvent
  | ForcesUpdateEvent
  | DoorOpenEvent // +spatial
  | DoorCloseEvent // +spatial
  | OvercapEvent // +lead: rotation rework (F12)
  | ObjectHitEvent // +world-resources
  | ObjectBreakEvent // +world-resources
  | ObjectDiscoveredEvent // +world-resources
  | CacheOpenEvent // +world-resources
  | PickupSpawnEvent // +world-resources
  | PickupEvent // +world-resources
  | ChatEvent // +personas
  | PresenceEvent // +personas
  | PartyLeaveEvent // +personas
  | JumpEvent // +jump
  | GlideEvent // +glide
  | LevelUpEvent // +field
  | PortalEvent // +field
  | FieldSpawnEvent // +field
  | WaystoneEvent // +zone
  | GatherEvent; // +prof

export type SimEventType = SimEvent['type'];

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
/** What subsystems pass to ctx.emit: timestamp is filled in by core. */
export type SimEventInput = DistributiveOmit<SimEvent, 'timestamp'>;

// ============================================================================
// 17. Content registry
// ============================================================================

/** Raw content as authored (what @keystone/content exports). */
export interface ContentBundle {
  classes: ClassDef[];
  npcs: NpcDef[];
  /** All enemy abilities (EnemyAbility extends AbilityDef). */
  enemyAbilities: EnemyAbility[];
  encounters: EncounterDef[];
  dungeons: DungeonDef[];
  /** Extra standalone abilities/auras not reachable from classes/NPCs. */
  abilities?: AbilityDef[];
  auras?: AuraDef[];
  /** Base HP per NPC tier (NpcDef.hpMult multiplies it). */
  tierHp: Record<NpcTier, number>;
  /** +specs: specialisations (their abilities and auras are indexed even while no class carries them). */
  specs?: SpecDef[];
}

/**
 * Indexed, read-only content. Built by `createRegistry(bundle)` in sim/core, which also
 * walks every effect tree to index inline AuraDefs by id.
 */
export interface ContentRegistry {
  readonly bundle: ContentBundle;
  ability(id: AbilityId): AbilityDef | undefined;
  /** Enemy ability (with timer/target rule) or undefined for player abilities. */
  enemyAbility(id: AbilityId): EnemyAbility | undefined;
  aura(id: AuraId): AuraDef | undefined;
  class(id: ClassId): ClassDef | undefined;
  npc(id: NpcId): NpcDef | undefined;
  encounter(id: EncounterId): EncounterDef | undefined;
  dungeon(id: DungeonId): DungeonDef | undefined;
  /** Base max HP for an NPC (tierHp[tier] * hpMult), before keystone scaling. */
  npcBaseHp(id: NpcId): number;
  readonly abilities: ReadonlyMap<AbilityId, AbilityDef>;
  readonly auras: ReadonlyMap<AuraId, AuraDef>;
  /** +specs: a specialisation by id. */
  spec(id: SpecId): SpecDef | undefined;
  /** +specs: every specialisation in the bundle (bundle order). */
  readonly specs: readonly SpecDef[];
}

// ============================================================================
// 18. Sim context and subsystem interfaces
// ============================================================================

/** Everything a subsystem gets each call. Subsystems mutate `world` directly. */
export interface SimContext {
  readonly world: World;
  readonly rng: Rng;
  readonly content: ContentRegistry;
  readonly sys: Subsystems;
  /** Current sim time (== world.time). */
  readonly now: Ms;
  /** Append an event to world.events (timestamp = now). */
  emit(e: SimEventInput): void;
  /** Allocate a unique id with a prefix ("n", "a", "t", "z"). */
  newId(prefix: string): string;
  /** World mutation helpers implemented by core. */
  readonly ops: WorldOps;
}

/** Spawning/despawning, implemented by core so every subsystem builds units the same way. */
export interface WorldOps {
  /** Create a party member from a ClassDef at `pos`. */
  spawnPlayer(spec: { classId: ClassId; name: string; controller: Controller; pos: Vec2; facing: Radians; id?: UnitId }): Unit;
  /** Create an NPC; applies keystone scaling via sys.mplus.scaling(). */
  spawnNpc(spec: { npcId: NpcId; pos: Vec2; facing: Radians; packId: PackId | null; summonerId?: UnitId }): Unit;
  /** Remove a unit entirely (summons that despawn). Dead units normally stay. */
  despawn(unitId: UnitId): void;
  /** Look up a unit (undefined if missing). */
  unit(id: UnitId): Unit | undefined;
  /** Alive units in deterministic order, optionally filtered. */
  units(filter?: (u: Unit) => boolean): Unit[];
}

/** Casting, GCD, cooldowns, spell queue, channels. Implemented by sim/core. */
export interface CastingSystem {
  /** Validate without side effects. */
  canCast(ctx: SimContext, casterId: UnitId, abilityId: AbilityId, targetId?: UnitId | null, point?: Vec2 | null): CastResult;
  /** Start (or queue, within SPELL_QUEUE_WINDOW_MS) a cast. Instants resolve immediately. */
  tryCast(ctx: SimContext, casterId: UnitId, abilityId: AbilityId, targetId?: UnitId | null, point?: Vec2 | null): CastResult;
  /** Advance casts/channels/queue for all units; completes casts via executeAbility. */
  update(ctx: SimContext): void;
  /** Resolve an ability's effects now (pays cost, triggers cooldown, emits 'cast'). */
  executeAbility(ctx: SimContext, casterId: UnitId, abilityId: AbilityId, targetId: UnitId | null, point: Vec2 | null): void;
  /** Interrupt the unit's cast (if interruptible); locks its school. Returns true if interrupted. */
  interrupt(ctx: SimContext, targetId: UnitId, sourceId: UnitId, byAbilityId: AbilityId, lockoutMs: Ms): boolean;
  /** Cancel a cast without lockout (moved, player cancel, death, CC). */
  cancelCast(ctx: SimContext, unitId: UnitId, reason: 'player' | 'moved' | 'cc' | 'death' | 'target_lost' | 'out_of_range'): void;
  /** Apply damage pushback to the unit's cast/channel. */
  pushback(ctx: SimContext, unitId: UnitId): void;
  /** Cooldown remaining for the next charge (0 if a charge is available). */
  cooldownRemaining(world: World, unitId: UnitId, abilityId: AbilityId): Ms;
  /** Hasted GCD length for this unit. */
  gcdDuration(ctx: SimContext, unitId: UnitId, ability: AbilityDef): Ms;
  modifyCooldown(ctx: SimContext, unitId: UnitId, abilityId: AbilityId, change: { deltaMs?: Ms; reset?: boolean; addCharge?: boolean }): void;
}

/** Effect application context passed through the effect pipeline. */
export interface EffectCtx {
  sourceId: UnitId;
  abilityId: AbilityId;
  /** Primary target (may be null for ground/self-centred). */
  targetId: UnitId | null;
  point: Vec2 | null;
  /** True for periodic ticks (DoT/HoT/zone). */
  periodic?: boolean;
  /** Aura instance this effect comes from (periodic/procs). */
  auraIid?: AuraInstanceId;
  /** Stat snapshot to use instead of live source stats. */
  snapshot?: AuraInstance['snapshot'];
  /** Recursion depth guard (procs → effects → procs). Max 4. */
  depth?: number;
  /**
   * +integrator: this call is the completion of a cast (casting.executeAbility / channel start).
   * Combat fires 'cast' procs only when true. Flat threat is added by casting, not combat.
   */
  isCast?: boolean;
  /** +lead: rotation rework (F3). Start of the resolving cast (hard casts: CastState.startedAt; default now). */
  castStartedAt?: Ms;
  /**
   * +sim: rotation rework (F3). world.nextSeq when the resolving cast started (default: at the
   * applyEffects call). Breaks same-millisecond ties in the guard: an aura instance created at or after
   * this sequence number belongs to this cast and is never consumed by it.
   */
  castStartSeq?: number;
  /** +mage-specs (S11): points the resolving cast's `AbilityDef.spendAll` took. */
  spent?: number;
}

export interface DamageRequest {
  sourceId: UnitId;
  targetId: UnitId;
  abilityId: AbilityId;
  school: School;
  /** Raw amount before mitigation (already includes source multipliers if `preScaled`). */
  amount: number;
  preScaled?: boolean;
  critBonus?: number;
  canCrit?: boolean;
  avoidable?: boolean;
  isAoE?: boolean;
  periodic?: boolean;
  threatMult?: number;
  /** +lead: rotation rework (F7). Stacks read by Amount.perStack (copied to the damage event). */
  stacks?: number;
  /** +autoattack: a white hit (copied to the damage event). */
  swing?: 'mh' | 'oh' | 'shot';
  /** +autoattack: extra miss chance on the attack table (dual wield white swings). */
  missBonus?: number;
  /** +specs: DamageEffect.selfHarm (no crit, no mitigation, no absorbs, no threat, never lethal). */
  selfHarm?: boolean;
}

export interface DamageResult {
  hitType: HitType;
  amount: number;
  absorbed: number;
  overkill: number;
  killed: boolean;
}

export interface HealRequest {
  sourceId: UnitId;
  targetId: UnitId;
  abilityId: AbilityId;
  amount: number;
  preScaled?: boolean;
  critBonus?: number;
  canCrit?: boolean;
  periodic?: boolean;
  /** +lead: rotation rework (F7). Stacks read by Amount.perStack (copied to the heal event). */
  stacks?: number;
}

export interface HealResult {
  hitType: 'hit' | 'crit';
  amount: number;
  overheal: number;
}

/** Damage/heal pipeline, auras, threat, melee, CC/DR, deaths, telegraph/zone resolution. */
export interface CombatSystem {
  /** Run a list of effects (resolves `to`/`area` via sys.spatial, evaluates conditions). */
  applyEffects(ctx: SimContext, effects: readonly EffectSpec[], ectx: EffectCtx): void;
  /** Full damage pipeline: avoidance → crit → mods → absorbs → HP → threat → procs → pushback → event. */
  dealDamage(ctx: SimContext, req: DamageRequest): DamageResult;
  heal(ctx: SimContext, req: HealRequest): HealResult;
  /** Apply/refresh/stack an aura (handles DR, immunity, pandemic, events). Returns the instance or null if resisted. */
  applyAura(ctx: SimContext, targetId: UnitId, auraId: AuraId, sourceId: UnitId, abilityId: AbilityId, opts?: { stacks?: number; absorb?: number; durationMs?: Ms }): AuraInstance | null;
  /** Remove by instance id or by aura id (+caster). Emits remove events, fires onRemove. */
  removeAura(ctx: SimContext, targetId: UnitId, which: { iid?: AuraInstanceId; auraId?: AuraId; casterId?: UnitId }, reason: 'expired' | 'dispelled' | 'cancelled' | 'death' | 'consumed' | 'replaced' | 'evade'): void;
  /** Advance periodic ticks and expirations for all units. */
  tickAuras(ctx: SimContext): void;
  /** Advance zones (ticks, inside-auras, expiry). */
  tickZones(ctx: SimContext): void;
  /** Resolve telegraphs whose resolvesAt <= now (hit test via sys.spatial). */
  tickTelegraphs(ctx: SimContext): void;
  /** Auto-attacks for every unit with a swing timer and a valid target. */
  tickMelee(ctx: SimContext): void;
  /** Resource regen/decay per tick. */
  tickResources(ctx: SimContext): void;
  /** Mark units at 0 HP dead (emit death, clear auras/casts, notify ai/mplus). Returns newly dead ids. */
  resolveDeaths(ctx: SimContext): UnitId[];
  /** Add threat for `sourceId` on NPC `npcId` (applies threatMult). Also puts both in combat. */
  addThreat(ctx: SimContext, npcId: UnitId, sourceId: UnitId, amount: number): void;
  /** Healing/resource threat: 0.5/pt split across every NPC fighting `healedId`. */
  addHealingThreat(ctx: SimContext, sourceId: UnitId, healedId: UnitId, amount: number): void;
  taunt(ctx: SimContext, npcId: UnitId, taunterId: UnitId, durationMs: Ms): void;
  /** Derived stats with aura mods (cheap enough to call often). */
  effectiveStats(ctx: SimContext, unitId: UnitId): EffectiveStats;
  controlState(ctx: SimContext, unitId: UnitId): ControlState;
  /** Revive with fraction of HP (wipe recovery / battle res). */
  revive(ctx: SimContext, unitId: UnitId, hpPct: number, pos?: Vec2): void;
  /** Evaluate a Condition (also used by casting usableIf and bots via queries). */
  evalCondition(ctx: SimContext, cond: Condition, sourceId: UnitId, targetId: UnitId | null): boolean;
  /** +integrator: threat victim rules live with threat; AI delegates selectVictim here when present. */
  pickVictim?(ctx: SimContext, npcId: UnitId): UnitId | null;
  reselectVictim?(ctx: SimContext, npcId: UnitId, force?: boolean): UnitId | null;
}

/** NPC brains: aggro, victim selection, EventMap timers, ability use, evade. */
export interface AISystem {
  /** Per-tick decisions for every NPC (aggro radius checks, victim reselect, abilities, movement). */
  update(ctx: SimContext): void;
  /** Pull a pack into combat (social aggro) with `attackerId` on the threat tables. */
  engagePack(ctx: SimContext, packId: PackId, attackerId: UnitId): void;
  /** Hook: NPC took damage / was targeted by a hostile action (starts combat if idle). */
  onAttacked(ctx: SimContext, npcId: UnitId, attackerId: UnitId): void;
  /** Hook: a unit died (remove from threat tables, encounter bookkeeping). */
  onDeath(ctx: SimContext, unitId: UnitId): void;
  /** Reset an NPC to home: drop auras/threat, reset cooldowns, full HP. */
  evade(ctx: SimContext, npcId: UnitId): void;
  /** Re-pick victim from threat table (respects fixate > taunt > threat and overtake rules). */
  selectVictim(ctx: SimContext, npcId: UnitId): UnitId | null;
  /** Start/advance/end boss encounters (phases by HP, enrage, wipe detection). */
  updateEncounter(ctx: SimContext): void;
}

/** Filter for spatial unit queries. */
export interface UnitQuery {
  /** Faction relative to `relativeTo` (required for 'enemies'/'allies'). */
  affects?: Affects;
  relativeTo?: UnitId;
  aliveOnly?: boolean; // default true
  excludeIds?: UnitId[];
}

/** Movement, collision, geometry queries, navigation. */
export interface SpatialSystem {
  /** Build nav data for the world's dungeon. Called once by createSim (and after restore). */
  init(ctx: SimContext): void;
  /** Integrate MoveIntents for all units by dtMs (speed from combat.effectiveStats), collide with walls. */
  moveUnits(ctx: SimContext, dtMs: Ms): void;
  /** Set a 'dest' MoveIntent with an A* path. Returns false if unreachable. */
  setDestination(ctx: SimContext, unitId: UnitId, dest: Vec2): boolean;
  /** A* path (list of waypoints, excluding `from`), or null. */
  findPath(world: World, from: Vec2, to: Vec2): Vec2[] | null;
  isWalkable(world: World, p: Vec2): boolean;
  /** Nearest walkable point to p. */
  clampToWalkable(world: World, p: Vec2): Vec2;
  roomAt(world: World, p: Vec2): RoomId | null;
  /** Center distance. */
  distance(a: Vec2, b: Vec2): Yards;
  /** Edge-to-edge distance including both combat reaches (WoW range checks). */
  rangeBetween(world: World, aId: UnitId, bId: UnitId): Yards;
  inMeleeRange(world: World, aId: UnitId, bId: UnitId): boolean;
  /** `range` is the ability range; adds reaches and moving leeway. */
  inRange(world: World, aId: UnitId, bId: UnitId, range: Yards): boolean;
  /** Is `bId` within `arc` (radians, total) in front of `aId`. */
  isInFront(world: World, aId: UnitId, bId: UnitId, arc: Radians): boolean;
  /** Is `aId` behind `bId` (outside b's 180° front). */
  isBehind(world: World, aId: UnitId, bId: UnitId): boolean;
  /** Place an AreaSpec/shape for a caster into the world. */
  resolveArea(world: World, shape: AreaShape, anchor: AreaAnchor, casterId: UnitId, targetId: UnitId | null, point: Vec2 | null): WorldShape;
  /** Is a point (with optional radius) inside a placed shape. */
  shapeContains(area: WorldShape, p: Vec2, radius?: Yards): boolean;
  /** Units (by id, deterministic order: closest first, then id) inside a placed shape. */
  unitsInShape(world: World, area: WorldShape, q: UnitQuery): UnitId[];
  unitsInRadius(world: World, center: Vec2, radius: Yards, q: UnitQuery): UnitId[];
  /** Nearest walkable point outside all given shapes (bot dodging). */
  nearestSafePoint(world: World, from: Vec2, danger: WorldShape[], radius: Yards): Vec2 | null;
  /** +patrols: nothing solid (walls, pillars, closed doors) between a and b. Missing = always clear. */
  lineOfSight?(world: World, a: Vec2, b: Vec2): boolean;
}

/** Keystone timer, forces, scaling, affixes, death penalty, wipes, completion. */
export interface MPlusSystem {
  /** Initialize world.mplus from config and dungeon. */
  init(ctx: SimContext, keystone: KeystoneConfig): void;
  /** HP/damage multipliers for an NPC at the current key level and affixes. */
  scaling(ctx: SimContext, npcId: NpcId): ScalingMults;
  /** Start the timer (on 'interact startRun' or first party movement out of the start area). */
  startRun(ctx: SimContext): void;
  /** Per-tick: timer, in-combat tracking + pull segments, wipe detection, completion. */
  update(ctx: SimContext): void;
  /** Hook from CombatSystem.resolveDeaths: forces for NPCs, death penalty for players. */
  onDeath(ctx: SimContext, unitId: UnitId): void;
  /** Party wipe: reset engaged packs, revive party at entrance. */
  onWipe(ctx: SimContext): void;
  /**
   * +integrator: a dead party member releases (interact 'releaseSpirit', or automatically once the
   * party has been out of combat for RELEASE_DELAY_MS) and respawns at the checkpoint (last killed
   * boss, else the entrance) with full HP. Deaths cost time (penalty + run back), not the run.
   */
  releaseSpirit?(ctx: SimContext, unitId: UnitId): void;
  /** Final result once phase is completed/depleted (or a provisional one while running). */
  result(world: World): RunResult;
}

/** Per-unit aggregated numbers for meters and harness metrics. */
export interface UnitMeters {
  unitId: UnitId;
  name: string;
  role: Role | null;
  classId: ClassId | null;
  damageDone: number;
  healingDone: number;
  overhealing: number;
  damageTaken: number;
  /** Damage taken from MELEE_ABILITY_ID. */
  meleeTaken: number;
  /** Damage taken flagged isAoE. */
  aoeTaken: number;
  selfHealing: number;
  absorbsDone: number;
  interrupts: number;
  dispels: number;
  deaths: number;
  casts: number;
  /** Ability id → count of 'cast' events. */
  castsByAbility: Record<AbilityId, number>;
  /** Ability id → damage done. */
  damageByAbility: Record<AbilityId, number>;
  /** Time in combat (ms) used for per-second rates. */
  activeMs: Ms;
}

export interface Meters {
  fromMs: Ms;
  toMs: Ms;
  units: Record<UnitId, UnitMeters>;
}

export interface PullSegment {
  pull: number;
  startMs: Ms;
  endMs: Ms | null;
  packIds: PackId[];
  mobs: number;
  isBoss: boolean;
  wipe: boolean;
}

/** WCL-shaped export compatible with tools/analyze_run.py. */
export interface WclExport {
  code: string;
  fight: {
    id: number;
    name: string;
    startTime: number;
    endTime: number;
    keystoneLevel: number;
    keystoneTime: number;
    keystoneBonus: number;
    kill: boolean;
    dungeonPulls: {
      id: number;
      name: string;
      startTime: number;
      endTime: number;
      kill: boolean;
      enemyNPCs: { gameID: number; id: number; minimumInstanceID: number; maximumInstanceID: number }[];
    }[];
  };
  masterData: {
    actors: { id: number; gameID: number; name: string; type: 'Player' | 'NPC' | 'Pet'; subType: string; petOwner: number | null }[];
    abilities: { gameID: number; name: string; type: string; icon: string }[];
  };
  /** Events with numeric ids (sourceID/targetID/abilityGameID) and WCL numeric hitType. */
  friendly: Record<string, unknown>[];
}

/** Event sink + aggregation. Implemented by sim/log. */
export interface CombatLog {
  /** Append events (called by core when a tick is flushed). */
  ingest(events: readonly SimEvent[], world: World): void;
  /** All events so far (optionally filtered). */
  events(filter?: { types?: SimEventType[]; sourceId?: UnitId; targetId?: UnitId; fromMs?: Ms; toMs?: Ms }): SimEvent[];
  /** Details!-style meters over a time window (default: whole run). */
  meters(window?: { fromMs?: Ms; toMs?: Ms }): Meters;
  /** Pull segments derived from pull_start/pull_end. */
  pulls(): PullSegment[];
  /** WCL-shaped JSON for tools/analyze_run.py. */
  exportWcl(world: World, content: ContentRegistry): WclExport;
  clear(): void;
}

export interface Subsystems {
  casting: CastingSystem;
  combat: CombatSystem;
  ai: AISystem;
  spatial: SpatialSystem;
  mplus: MPlusSystem;
  log: CombatLog;
  /** +encounters: boss/enemy mechanics (ai/mechanics). Optional so test fakes keep working. */
  mechanics?: MechanicsSystem;
}

// ============================================================================
// 19. Public sim API
// ============================================================================

export interface PartyMemberConfig {
  classId: ClassId;
  name: string;
  controller: 'human' | 'bot';
  /** Fixed id (default p1..p4 in order). */
  id?: UnitId;
  /** +field: hero level (levelMult on max HP and power; ClassDef.unlocks gate abilities). Absent = unlevelled. */
  level?: number;
}

export interface SimConfig {
  seed: number;
  content: ContentRegistry;
  dungeonId: DungeonId;
  keystone: KeystoneConfig;
  /** Party in slot order (tank, healer, dps, dps). */
  party: PartyMemberConfig[];
  /** Override subsystems (tests, alternative implementations). Missing = default. */
  subsystems?: Partial<Subsystems>;
  /** Spawn the dungeon's packs on creation (default true). Tests can set false and spawn manually. */
  spawnPacks?: boolean;
  /** Start the M+ timer immediately (default false: waits for interact startRun or first move). */
  autoStart?: boolean;
  /**
   * +world-resources: the unit that breaks objects and collects pickups. Default: the first
   * 'human' party member (none in an all-bot run, so bots never break or collect). Pass a bot id
   * for autopilot/spectate; null disables world loot.
   */
  looterId?: UnitId | null;
  /** +world-resources: set false to skip spawning DungeonDef.worldObjects (default true). */
  worldObjects?: boolean;
  /** +field: where the party arrives on a field map (default: the map entrance). */
  arrive?: { pos: Vec2; facing: Radians };
}

/** Read-only helpers for bots, HUD and harness (no mutation, no RNG use). */
export interface SimQueries {
  canCast(casterId: UnitId, abilityId: AbilityId, targetId?: UnitId | null, point?: Vec2 | null): CastResult;
  cooldownRemaining(unitId: UnitId, abilityId: AbilityId): Ms;
  gcdRemaining(unitId: UnitId): Ms;
  effectiveStats(unitId: UnitId): EffectiveStats;
  controlState(unitId: UnitId): ControlState;
  /** +lead: rotation rework (F2). What a press of `abilityId` casts right now (the override, or itself). */
  resolveAbility?(unitId: UnitId, abilityId: AbilityId): AbilityId;
  /** +lead: rotation rework (F11). Ability ids that should show the spell-activation glow now. */
  activeHighlights?(unitId: UnitId): Set<AbilityId>;
  /** Spatial read-only queries. */
  spatial: Pick<
    SpatialSystem,
    | 'findPath'
    | 'isWalkable'
    | 'clampToWalkable'
    | 'roomAt'
    | 'distance'
    | 'rangeBetween'
    | 'inMeleeRange'
    | 'inRange'
    | 'isInFront'
    | 'isBehind'
    | 'resolveArea'
    | 'shapeContains'
    | 'unitsInShape'
    | 'unitsInRadius'
    | 'nearestSafePoint'
  >;
}

export interface Sim {
  readonly content: ContentRegistry;
  readonly log: CombatLog;
  readonly queries: SimQueries;
  /** Apply commands at the tick boundary, advance one TICK_MS, return events emitted this tick. */
  step(commands?: readonly Command[]): SimEvent[];
  /** Live world (treat as read-only outside the sim). */
  getWorld(): Readonly<World>;
  /** Deep copy of the full state (JSON-safe). */
  snapshot(): World;
  /** Replace state with a snapshot. */
  restore(snapshot: World): void;
  /** Current run result (MPlusSystem.result). */
  result(): RunResult;
}

// ============================================================================
// 20. Bots
// ============================================================================

/** Deep read-only view used by bots and renderers. */
export type DeepReadonly<T> = T extends (infer R)[]
  ? readonly DeepReadonly<R>[]
  : T extends object
    ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
    : T;

export interface BotView {
  readonly world: DeepReadonly<World>;
  readonly selfId: UnitId;
  readonly content: ContentRegistry;
  readonly queries: SimQueries;
  readonly now: Ms;
  /**
   * +personas: events of the previous step (optional). Persona chat/tilt reads killing blows and
   * chat from it; without it personas still work from world state alone.
   */
  readonly events?: readonly SimEvent[];
}

export interface BotSkill {
  /** Delay between seeing something and acting on it (150–600 ms). */
  reactionMs: Ms;
  /** Probability an assigned kick actually happens (0.6–0.98). */
  kickReliability: number;
  /** Probability of dodging a telegraph in time (0.5–0.95). */
  dodgeReliability: number;
  /** Probability of choosing the APL's top valid action (0.7–1). */
  rotationAccuracy: number;
}

export type BotSkillPreset = 'novice' | 'average' | 'skilled' | 'elite' | 'competent' | 'expert'; // +integrator: competent/expert (PLAN §7 names)

export const BOT_SKILL_PRESETS: Readonly<Record<BotSkillPreset, BotSkill>> = {
  novice: { reactionMs: 600, kickReliability: 0.6, dodgeReliability: 0.5, rotationAccuracy: 0.7 },
  average: { reactionMs: 400, kickReliability: 0.8, dodgeReliability: 0.7, rotationAccuracy: 0.85 },
  skilled: { reactionMs: 250, kickReliability: 0.9, dodgeReliability: 0.85, rotationAccuracy: 0.95 },
  elite: { reactionMs: 150, kickReliability: 0.98, dodgeReliability: 0.95, rotationAccuracy: 1 },
  competent: { reactionMs: 350, kickReliability: 0.85, dodgeReliability: 0.75, rotationAccuracy: 0.88 },
  expert: { reactionMs: 160, kickReliability: 0.97, dodgeReliability: 0.93, rotationAccuracy: 0.98 },
};

/** A bot. Must be deterministic given its seed and the views it receives. */
export interface BotBrain {
  readonly unitId: UnitId;
  readonly skill: BotSkill;
  /** Called every tick; bots self-throttle (PLAN §7: think every 100 ms + reaction delay). */
  decide(view: BotView, now: Ms): Command[];
}

// ============================================================================
// 21. World resources (+world-resources; docs/design/world-resources.md)
// ============================================================================

/** +world-resources: what lies in the world. Progression maps these onto its wallet. */
export type WorldResourceKind =
  | 'gold'
  | 'fangsteel'
  | 'scale'
  | 'scaleUp'
  | 'venom'
  | 'moltskin'
  | 'eyeFragment'
  /** +field: bonus hero XP (XP wisps, chests, the treasure sprite). */
  | 'xp'
  /** +field: a gear drop (amount 1; the pickup's rarity is the item's; progression rolls the item). */
  | 'item';
/** +world-resources: amounts per kind ('scale' = the key's Coil Scale grade, 'scaleUp' = one grade above). */
export type WorldFinds = Partial<Record<WorldResourceKind, number>>;
/** +world-resources: renderer glow/beam colour class. */
export type PickupRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export const PICKUP_RARITY: Readonly<Record<WorldResourceKind, PickupRarity>> = {
  gold: 'common',
  fangsteel: 'common',
  scale: 'uncommon',
  moltskin: 'rare',
  venom: 'rare',
  scaleUp: 'epic',
  eyeFragment: 'legendary',
  xp: 'uncommon', // +field
  item: 'uncommon', // +field (each item pickup carries its own rarity)
};

/** +world-resources: breakable kinds (renderer picks the mesh), caches and reagent nodes. */
export type WorldObjectKind =
  | 'urn'
  | 'crate'
  | 'bone_pile'
  | 'egg_clutch'
  | 'cocoon'
  | 'offering_bowl'
  | 'hidden_cache'
  | 'venom_gland'
  | 'eye_shard'
  /** +field: a treasure chest that spawns in a field (opens like a cache; loot rolled at spawn). */
  | 'treasure_chest';
export type WorldObjectClass = 'breakable' | 'cache' | 'node';

/** +world-resources: one loot line. Rolled once per object from (run seed, object id). */
export interface WorldLootEntry {
  resource: WorldResourceKind;
  /** 0..1 */
  chance: number;
  min: number;
  max: number;
  /** Only on keys at or above this level (e.g. Eye fragments). */
  minLevel?: number;
}

export interface WorldObjectDef {
  id: string;
  kind: WorldObjectKind;
  class: WorldObjectClass;
  pos: Vec2;
  facing?: Radians;
  roomId: RoomId;
  /** Hits to break (1 = any hit). Caches ignore hits and open by standing at them. */
  hp: number;
  /** Footprint radius (yd): hit splash and interact reach are measured to its edge. */
  radius: Yards;
  loot: WorldLootEntry[];
  /** Hidden caches: undiscovered until the looter comes within WORLD_REVEAL_RADIUS. */
  hidden?: boolean;
  /** Content placement tag: on the walked route, beside it, or off it (exploring). */
  zone: 'route' | 'side' | 'off';
  /** Renderer hint (scale/variant 0..1). */
  variant?: number;
}

export interface WorldObjectState {
  id: string;
  kind: WorldObjectKind;
  class: WorldObjectClass;
  pos: Vec2;
  facing: Radians;
  roomId: RoomId;
  radius: Yards;
  hp: number;
  maxHp: number;
  hidden: boolean;
  /** Hidden objects: discovered by the looter (always true otherwise). */
  discovered: boolean;
  broken: boolean;
  brokenAt: Ms | null;
  /** Last hit (renderer hit-flash / wobble). */
  lastHitAt: Ms | null;
  /** Caches: time the looter has stood at it (opens at CACHE_OPEN_MS). */
  openProgressMs: number;
  variant: number;
  /** +field: loot rolled when the object spawned (field chests); replaces the DungeonDef table. */
  loot?: { resource: WorldResourceKind; amount: number; rarity?: PickupRarity }[];
  /** +field: despawns unopened at this time (field chests). */
  expiresAt?: Ms;
  /** +glide: stands on a platform at this height (opened only by a hero standing up there). */
  z?: Yards;
}

export interface PickupState {
  id: string;
  resource: WorldResourceKind;
  amount: number;
  rarity: PickupRarity;
  objectId: string;
  /** Where it burst from (the object) and where it lands. */
  from: Vec2;
  pos: Vec2;
  spawnedAt: Ms;
  /** Arc ends (renderer lerps from -> pos with a hop until then); no magnet before. */
  landAt: Ms;
  /** Set once the looter came within the magnet radius: flies toward the looter. */
  magnetSince: Ms | null;
  /** +field: an XP wisp (no magnet: run through it; chained wisps pay more). */
  wisp?: boolean;
  /** +field: vanishes uncollected at this time (field drops). */
  expiresAt?: Ms;
  /** +glide: burst from a chest up on a platform: drawn at this height. */
  z?: Yards;
}

export interface WorldLootStats {
  broken: number;
  breakables: number;
  cachesOpened: number;
  caches: number;
  nodes: number;
  nodesBroken: number;
  pickups: number;
}

export interface WorldLootState {
  looterId: UnitId | null;
  collected: WorldFinds;
  stats: WorldLootStats;
}

/** Splash radius around a struck enemy / AoE that breaks clutter (yd, to the object's edge). */
export const WORLD_HIT_SPLASH: Yards = 2;
/** Reach of a cast / swing / interact from the looter (yd, edge to edge). */
export const WORLD_TOUCH_REACH: Yards = 3;
/** The looter running into a breakable (edge to edge, while moving) kicks it over (yd). */
export const WORLD_BUMP_REACH: Yards = 0.4;
/** Pickups fly to the looter inside this radius (yd). */
export const PICKUP_MAGNET_RADIUS: Yards = 3;
/**
 * +world-resources: for a short window after landing, a fresh drop chases the looter from further
 * out, so loot from clutter kicked over on the run follows you instead of landing behind you.
 */
export const PICKUP_FRESH_MAGNET_RADIUS: Yards = 7;
/** How long after landing a drop uses PICKUP_FRESH_MAGNET_RADIUS (ms); later, PICKUP_MAGNET_RADIUS. */
export const PICKUP_FRESH_MS: Ms = 2500;
/** Pickups are collected inside this radius (yd). */
export const PICKUP_COLLECT_RADIUS: Yards = 1;
/** Hidden caches reveal inside this radius (yd). */
export const WORLD_REVEAL_RADIUS: Yards = 12;
/** Standing at a cache (within WORLD_TOUCH_REACH, out of combat or not) this long opens it. */
export const CACHE_OPEN_MS: Ms = 1500;

/** +world-resources: an object took a hit but did not break. */
export interface ObjectHitEvent extends EventBase {
  type: 'object_hit';
  sourceID: UnitId;
  objectId: string;
  kind: WorldObjectKind;
  hp: number;
}
/** +world-resources: an object broke (or a cache opened). */
export interface ObjectBreakEvent extends EventBase {
  type: 'object_break';
  sourceID: UnitId;
  objectId: string;
  kind: WorldObjectKind;
  objectClass: WorldObjectClass;
  pos: Vec2;
  /** Number of pickups it dropped. */
  drops: number;
}
/** +world-resources: a hidden object was spotted by the looter. */
export interface ObjectDiscoveredEvent extends EventBase {
  type: 'object_discovered';
  sourceID: UnitId;
  objectId: string;
  kind: WorldObjectKind;
  pos: Vec2;
}
/** +world-resources: the looter started (or abandoned) opening a cache. */
export interface CacheOpenEvent extends EventBase {
  type: 'cache_open';
  sourceID: UnitId;
  objectId: string;
  state: 'start' | 'cancel';
  durationMs: Ms;
}
/** +world-resources: loot burst out of an object. */
export interface PickupSpawnEvent extends EventBase {
  type: 'pickup_spawn';
  pickupId: string;
  objectId: string;
  resource: WorldResourceKind;
  amount: number;
  rarity: PickupRarity;
  from: Vec2;
  pos: Vec2;
  landAt: Ms;
  /** +field: an XP wisp (renderer: floating orb, no arc). */
  wisp?: boolean;
}
/** +world-resources: the looter picked something up. */
export interface PickupEvent extends EventBase {
  type: 'pickup';
  sourceID: UnitId;
  pickupId: string;
  resource: WorldResourceKind;
  amount: number;
  rarity: PickupRarity;
  pos: Vec2;
  /** Running total of this resource collected this run. */
  total: number;
  /** +field: XP wisps collected in a row (this one included; its amount already has the bonus). */
  chain?: number;
}

// ============================================================================
// 20. +field: open fields, towns and levels (docs/design/zones.md Phase 1)
// ============================================================================

/** A spawn point: keeps `count` mobs of its family alive, respawning each `respawnMs` after it dies. */
export interface FieldSpawnDef {
  id: string;
  /** Mob family: each (re)spawn picks one of these (seeded). */
  npcIds: NpcId[];
  pos: Vec2;
  /** Mobs kept alive here (default 1), scattered within `radius`. */
  count?: number;
  /** Scatter and wander radius (yards). */
  radius: Yards;
  /** Mob level range, inclusive; rolled per (re)spawn. */
  level: [number, number];
  /** Respawn delay after a death (default FieldDef.respawnMs); a [min, max] range is rolled per death (seeded jitter). */
  respawnMs?: Ms | readonly [Ms, Ms];
  /** +zone: a rare named mob (long timer; nameplate / tracker may flag it). */
  rare?: boolean;
  /** Out-of-combat movement (default: wander within `radius`). */
  movement?: SpawnMovement;
  /** Elite mobs (nameplate mark, XP bonus). */
  elite?: boolean;
  /** Aggro radius of these mobs (default FIELD_AGGRO_RADIUS; 0 = passive until attacked). */
  aggroRadius?: Yards;
  /** +specs: a fixed spawn facing (no roll; with radius 0 the spawn draws no randomness at all). */
  facing?: Radians;
}

/** Where a portal leads. Walking into it emits 'portal'; the client loads the destination map. */
export interface PortalDef {
  id: string;
  /** Label ("To Bramblewood"). */
  name: string;
  pos: Vec2;
  radius: Yards;
  /** Destination map id (a field / town DungeonDef, or a dungeon for kind 'dungeon'). */
  to: DungeonId;
  /** Arrival portal on the destination map: arrive in front of it. Absent = the map entrance. */
  toPortal?: string;
  kind: 'portal' | 'gate' | 'dungeon';
  /** Facing of the portal mouth (radians); arrivals step out along it. */
  facing: Radians;
  /** Recommended level (prompts / labels). */
  minLevel?: number;
}

export type FieldNpcRole = 'questgiver' | 'bounty_board' | 'forge' | 'dungeon_board' | 'vendor' | 'gear' | 'guard' | 'villager' | 'lore' | 'waystone';

/** A town NPC or board: renderer + HUD only (not a sim unit; never targetable or attackable). */
export interface FieldNpcDef {
  id: string;
  name: string;
  /** Subtitle under the name ("Bounty Board", "Blacksmith"). */
  title?: string;
  role: FieldNpcRole;
  pos: Vec2;
  facing: Radians;
  /** Renderer model hint (e.g. a hero model id, 'board', 'anvil'). */
  model?: string;
}

/**
 * +zone: a named region or sub-area of an open map (docs/design/zones.md "Mossvale"): the
 * discovery toast, the tracker header, Auto's hunting ground and the renderer's biome. Areas may
 * overlap; `fieldAreaAt` returns the first one (definition order) containing the point, so
 * sub-areas are listed before their region.
 */
export interface FieldAreaDef {
  id: string;
  name: string;
  shape: Polygon;
  /** Parent region (sub-areas). */
  region?: string;
  /** Recommended level band (tracker header; loot level). */
  levelBand?: [number, number];
  /** No hunting here (Auto refuses; towns). */
  safe?: boolean;
  /** Renderer biome ('meadow' | 'forest' | 'deepforest' | 'rocky' | 'lake' | 'town' | 'ruins'). */
  biome?: string;
  /** First-visit discovery bonus in kill-units of XP at the area's level (absent = name toast only). */
  discoverKills?: number;
}

/** +zone: a hidden treasure on an open map (a chest that comes back `respawnMs` after it is opened). */
export interface FieldSecretDef {
  id: string;
  pos: Vec2;
  kind: 'chest';
  /** Loot level (default: the area's). */
  level?: number;
  respawnMs?: Ms;
  /** +glide: on a platform at this height (a sky-puzzle chest: reached by jumping / gliding). */
  z?: Yards;
  /** +glide: a sky-puzzle chest: richer than a ground secret. */
  sky?: boolean;
}

/**
 * +zone: a waystone on an open map. Interact `what: 'waystone:<id>'` next to any waystone (within
 * WAYSTONE_USE_YD, out of combat, alive, off cooldown) moves the hero to that waystone. Which stones a
 * hero may pick (attunement) and the channel bar are the client's; the sim checks place and state.
 */
export interface FieldWaystoneDef {
  id: string;
  name: string;
  pos: Vec2;
}
/** +zone: how close a hero must stand to a waystone to use it. */
export const WAYSTONE_USE_YD = 8;
/** +zone: a hero's waystone cooldown (sim-enforced). */
export const WAYSTONE_COOLDOWN_MS = 60_000;

/** +zone: respawn pacing for big open maps (all optional; absent = Phase 1 behaviour). */
export interface FieldRespawnRules {
  /** A slot whose spawn point has a living hero within this many yards waits (re-checked every second). */
  blockYd?: Yards;
  /** A slot never waits longer than this past its time for a blocking hero (then spawns anyway). */
  blockMaxMs?: Ms;
  /** Continuous clearing quickens the next respawn: −`perKill` per kill at this point in the last `windowMs`, down to `floorMs`. */
  quicken?: { perKill: number; windowMs: Ms; floorMs: Ms };
}

export interface FieldDef {
  kind: 'field' | 'town';
  zoneId: string;
  /** Recommended level band. */
  levelBand: [number, number];
  spawns: FieldSpawnDef[];
  portals: PortalDef[];
  npcs?: FieldNpcDef[];
  /** Default respawn delay (ms); a [min, max] range is rolled per death. */
  respawnMs: Ms | readonly [Ms, Ms];
  /** +zone: respawn pacing (player-proximity hold, dynamic quickening). */
  respawn?: FieldRespawnRules;
  /** +zone: named regions / sub-areas (discovery, tracker header, Auto's ground, biomes). */
  areas?: FieldAreaDef[];
  /** +zone: hidden chests. */
  secrets?: FieldSecretDef[];
  /** +zone: waystones (fast travel between attuned stones). */
  waystones?: FieldWaystoneDef[];
  /** +prof: herb and ore nodes (docs/design/professions.md; content zones/mossvaleNodes.ts). */
  gatherNodes?: GatherNodeDef[];
  /**
   * +zone: dormant spawn points. A point with no living hero within this many yards (and no unit of
   * its own in combat) parks its live mobs (they leave the world; their slots are due again at once),
   * and fills them back when a hero comes within 0.85 × this. Respawn timers keep running while
   * dormant. Absent = every point is always live.
   */
  dormantYd?: Yards;
  /** Mobs farther than this from their spawn point evade home. */
  leashRadius: Yards;
  /** Renderer theme id ('meadow' | 'forest' | 'town' ...). */
  theme: string;
  /** Ground loot (gold piles, XP wisps, chests, the treasure sprite). Absent = none (towns). */
  loot?: FieldLootDef;
  /**
   * Auras every party unit gets on this map, by role (registered in ContentBundle.auras). Phase 1
   * uses it for the solo damage boost that lets a tank or healer grind at a fair pace.
   */
  partyAuras?: Partial<Record<Role, AuraId>>;
}

/** Live state of one spawn point (index = slot 0..count-1). */
export interface FieldSpawnState {
  unitIds: (UnitId | null)[];
  /** When each empty slot respawns (null = alive / not scheduled). */
  respawnAt: (Ms | null)[];
  /** +zone: recent kill times at this point (dynamic respawn), oldest first. */
  kills?: Ms[];
  /** +zone: when each slot was first due (a blocking hero holds it at most blockMaxMs). */
  dueAt?: (Ms | null)[];
  /** +zone: parked (FieldDef.dormantYd): no hero near; live mobs left the world, slots wait for one. */
  dormant?: boolean;
}

export interface FieldState {
  spawns: Record<string, FieldSpawnState>;
  /** Enemy kills on this map so far. */
  kills: number;
  /** +field: where dead heroes respawn (SimConfig.arrive, else the map entrance). */
  arrive?: { pos: Vec2; facing: Radians };
  /** +field: portal each player unit currently stands in (edge trigger: 'portal' fires on entry only). */
  portalInside?: Record<UnitId, string | null>;
  /** +field: last time each player unit was in combat (out-of-combat regen starts FIELD_REGEN_DELAY_MS later). */
  lastCombatAt?: Record<UnitId, Ms>;
  /** +field: next corpse sweep (dead NPCs despawn FIELD_CORPSE_MS after death). */
  nextSweepAt?: Ms;
  /** +zone: when each hero may use a waystone again. */
  waystoneReadyAt?: Record<UnitId, Ms>;
  /** +field: world pickups on this map (field/loot.ts). */
  loot?: FieldLootState;
  /** +prof: node depletion, gathering channels and the heroes' profession skills (field/gather.ts). */
  gather?: GatherState;
}

// +prof: gathering professions (docs/design/professions.md) ------------------------------------

export type GatherProfession = 'foraging' | 'mining';

/** +prof: a herb / ore node on an open map. Interact `what: 'gather:<id>'` within GATHER_REACH channels it. */
export interface GatherNodeDef {
  id: string;
  /** Node kind (content GATHER_NODE_KINDS: name, mesh, yields). */
  kind: string;
  profession: GatherProfession;
  /** 1..4 (higher = rarer, better yields). */
  tier: number;
  /** Minimum profession skill to gather it. */
  req: number;
  pos: Vec2;
  facing?: Radians;
  /** Channel length. */
  channelMs: Ms;
  /** Depleted for a seeded [min, max] after a gather. */
  respawnMs: readonly [Ms, Ms];
}

/** +prof: a gather in progress (interrupted by moving, damage, combat, casting or death). */
export interface GatherChannelState {
  nodeId: string;
  startAt: Ms;
  endAt: Ms;
  /** Where the hero stood and its HP when it started. */
  pos: Vec2;
  hp: number;
}

export interface GatherState {
  /** Nodes gathered at least once: when they are back and how often they were gathered (absent = ready). */
  nodes: Record<string, { readyAt: Ms; gathers: number }>;
  channels: Record<UnitId, GatherChannelState>;
  /** Profession skills per hero (setGatherSkill; absent = 1). */
  skill: Record<UnitId, Partial<Record<GatherProfession, number>>>;
}

/** +prof: gather reach (yards, hero centre to node centre). */
export const GATHER_REACH: Yards = 3.5;

/** +field: what a field's ground loot does (FieldDef.loot; defaults in field/loot.ts). */
export interface FieldLootDef {
  /** Gold pile from a kill: chance per kill. */
  killGoldChance: number;
  /** Gear drop from a kill: chance per kill (elites always drop one). */
  killItemChance: number;
  /** Ambient spawns: [min, max] ms between them (0 / absent = never). */
  wispEveryMs: [Ms, Ms];
  pouchEveryMs: [Ms, Ms];
  chestEveryMs: [Ms, Ms];
  spriteEveryMs: [Ms, Ms];
  /** The treasure sprite's NpcDef (a fleeing mob with a big payout). */
  spriteNpcId?: NpcId;
}

/** +field: live state of a field's ground loot (timers, the chest, the sprite, the wisp chain). */
export interface FieldLootState {
  nextWispAt: Ms;
  nextPouchAt: Ms;
  nextChestAt: Ms;
  nextSpriteAt: Ms;
  /** XP wisps picked up in a row (resets WISP_CHAIN_MS after the last). */
  chain: { count: number; lastAt: Ms };
  /** The live treasure sprite and when it escapes. */
  spriteId: UnitId | null;
  spriteUntil: Ms;
  /** Id counter for pickups / chests. */
  seq: number;
  /** +zone: secret chest id → when it comes back (absent = placed / never opened). */
  secretsAt?: Record<string, Ms>;
}

/** +field: default aggro radius of field mobs (yards). */
export const FIELD_AGGRO_RADIUS: Yards = 9;
/** +basekits: a stealthed hero (AuraDef.flags.stealth) is only noticed by proximity this close. */
export const STEALTH_DETECT_RADIUS: Yards = 3;
/** +zone: out of combat, field mobs with no player unit within this box (yd, per axis) skip their AI (sleep). */
export const FIELD_AI_SLEEP_YD: Yards = 110;
/** +field: a dead hero in a field respawns at the map entrance after this long. */
export const FIELD_RELEASE_MS: Ms = 5000;
/** +field: dead NPCs on a field map are removed from the world this long after death. */
export const FIELD_CORPSE_MS: Ms = 6000;
/** +field: heroes in a field regenerate HP / mana once out of combat this long. */
export const FIELD_REGEN_DELAY_MS: Ms = 5000;
/** +field: out-of-combat regen in a field, fraction of max HP / mana per second. */
export const FIELD_REGEN_PCT_PER_SEC = 0.08;

// ============================================================================
// 21. +jump
// ============================================================================

/** Airborne time of a jump (ms) and the apex height renderers draw (yards). */
export const JUMP_MS: Ms = 600;
export const JUMP_HEIGHT: Yards = 1.25;

/** Height of a jumping unit at `timeMs` (0 on the ground): a ballistic arc 4h·t(1−t). */
export function jumpHeightAt(jump: { at: Ms; until: Ms } | undefined | null, timeMs: Ms): number {
  if (!jump || timeMs <= jump.at || timeMs >= jump.until) return 0;
  const t = (timeMs - jump.at) / (jump.until - jump.at);
  return 4 * JUMP_HEIGHT * t * (1 - t);
}

// ============================================================================
// 22. +glide (free flight: the glider, falls, floating platforms, updrafts)
// ============================================================================

/**
 * +glide: a unit in free flight (Unit.jump.air). `z` is the absolute height (yd), `vz` its rate,
 * `vel` the ground-plane velocity. Gliding: slow sink, forward drift along the facing, steered by
 * the held move direction (Unit.jump.pending ?? the move at take-off). Folded: a ballistic fall.
 */
export interface AirState {
  z: Yards;
  vz: number;
  vel: Vec2;
  glide: boolean;
  /** Highest point of this flight (the landing reports the drop). */
  peak: Yards;
  /** The glider was folded mid-air: the fall bleeds its drift (a hop keeps its run). */
  folded?: boolean;
  /** Set by the spatial step when the unit touched down this tick (the core lands it). */
  landed?: boolean;
}

/** +glide: a floating platform: a disc of radius `r` whose walkable top is at height `top`. */
export interface PlatformDef {
  id: string;
  pos: Vec2;
  r: Yards;
  top: Yards;
  /** Renderer look ('rock' | 'crystal' | 'scaffold' | 'ruin'). */
  kind?: string;
  /** Sky puzzle it belongs to (discovery toast, renderer grouping). */
  puzzle?: string;
}
/** +glide: an updraft (wind chute): a column of radius `r` from `base` to `top` (absolute heights). */
export interface UpdraftDef {
  id: string;
  pos: Vec2;
  r: Yards;
  base: Yards;
  top: Yards;
}
/** +glide: a sky puzzle (a route of platforms by a chute, a chest on top). */
export interface SkyPuzzleDef {
  id: string;
  name: string;
  /** The top platform (reaching it shows the discovery toast). */
  topId: string;
  /** Secret id of its chest (FieldDef.secrets). */
  chestId: string;
  /** The intended route: it starts on the ground at `start`, then each leg reaches platform `to` by a hop, a glide, or a ride up `updraft`. */
  start: Vec2;
  route: readonly { to: string; via: 'jump' | 'glide' | 'updraft'; updraft?: string }[];
}
export interface AirspaceDef {
  /**
   * Floor height function id (registerAirFloor): the terrain, or the water surface over water.
   * Content is plain data (cloned, serialised), so the function lives in a registry. Absent = 0.
   */
  floor?: string;
  /**
   * How far each obstacle (DungeonDef.obstacles index) stands above the floor: 0 = the terrain itself
   * (cliffs, deep water), more for props (trees, houses). Missing = solid at any height.
   */
  obstacleTops?: readonly number[];
  platforms?: readonly PlatformDef[];
  updrafts?: readonly UpdraftDef[];
  puzzles?: readonly SkyPuzzleDef[];
}

/** +glide: tuning (yd, s). Run speed is 7 yd/s. */
export const GLIDE = {
  /** Forward drift along the facing; W adds `boost`, S takes `brake` (×1.14 run, up to ×1.29, down to ×0.8). */
  speed: 8,
  boost: 1,
  brake: 2.4,
  /** Sideways speed from A / D (strafe-steer). */
  strafe: 4.2,
  /** Sink rate (yd/s). */
  sink: 3,
  /** Approach rates per second (floaty: the glider eases toward its target velocity). */
  accel: 2.2,
  vAccel: 4,
  /** Free fall (folded): gravity matching the hop's arc, terminal speed, horizontal drag per second. */
  gravity: (8 * JUMP_HEIGHT) / ((JUMP_MS / 1000) * (JUMP_MS / 1000)),
  fallMax: 30,
  fallDrag: 1.6,
  /** Updraft lift (yd/s) and the band under the top over which it eases out. */
  lift: 8,
  liftEase: 6,
  /** Inside an updraft the drift is only what the keys ask (`chuteSpeed` × speed), and the column pulls toward its axis. */
  chuteSpeed: 0.55,
  chutePull: 1.5,
  /** Higher ground than this above the glider blocks it (less is a step it lands on). */
  step: 0.5,
} as const;
/** +glide: the hop's take-off rate (yd/s) for a jump in free flight (from a platform). */
export const JUMP_VZ = (4 * JUMP_HEIGHT) / (JUMP_MS / 1000);
/** +glide: rate (yd/s) of a classic hop at `timeMs` (the derivative of jumpHeightAt). */
export function jumpRateAt(jump: { at: Ms; until: Ms }, timeMs: Ms): number {
  const T = (jump.until - jump.at) / 1000;
  const t = Math.max(0, Math.min(1, (timeMs - jump.at) / (jump.until - jump.at)));
  return T > 0 ? (4 * JUMP_HEIGHT * (1 - 2 * t)) / T : 0;
}
/** +glide: airborne (a hop in progress, or free flight). */
export function isAirborne(u: { jump?: { until: Ms; air?: unknown } }, timeMs: Ms): boolean {
  return !!u.jump && (!!u.jump.air || timeMs < u.jump.until);
}

/** +glide: floor height functions by id (AirspaceDef.floor); content registers its terrain at import. */
const AIR_FLOORS = new Map<string, (x: number, y: number) => number>();
export function registerAirFloor(id: string, fn: (x: number, y: number) => number): void {
  AIR_FLOORS.set(id, fn);
}
export function airFloorFn(id: string | undefined): ((x: number, y: number) => number) | null {
  return id ? (AIR_FLOORS.get(id) ?? null) : null;
}
